import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useConsultationDetail } from '../hooks/useConsultationDetail';
import EmptyState from '../../../components/ui/EmptyState';
import { consultationDiagnosisService } from '../../../services/consultationDiagnosisService';
import { consultationPrescriptionService } from '../../../services/consultationPrescriptionService';
import { diagnosisService } from '../../../services/diagnosisService';
import { medicalReferralService } from '../../../services/medicalReferralService';
import { doctorService } from '../../../services/doctorService';
import { consultationImagingOrderService } from '../../../services/consultationImagingOrderService';
import { consultationLabOrderService } from '../../../services/consultationLabOrderService';
import { imagingStudyService } from '../../../services/imagingService';
import labTestService from '../../../services/labTestService';
import type { LabTest } from '../../../services/labTestService';
import apiClient from '../../../services/apiClient';
import { consultationService } from '../../../services/consultationService';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { patientMedicalHistoryService, medicalHistoryTypeService, type PatientMedicalHistory } from '../../../services/patientMedicalHistoryService';
import type { ConsultationDiagnosis, ConsultationPrescription, Diagnosis } from '../../../types/api';
import type { MedicalReferral } from '../../../services/medicalReferralService';
import type { ConsultationImagingOrder } from '../../../services/consultationImagingOrderService';
import type { ConsultationLabOrder } from '../../../services/consultationLabOrderService';
import type { ImagingStudy } from '../../../services/imagingService';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import { formatDate } from '../../../lib/format';
import { useCanSign, canEditConsultation, isConsultationReadOnly, signErrorMessage } from '../hooks/useConsultationAccess';
import ConsultationStatusBadge from '../components/ConsultationStatusBadge';
import AmendmentDrawer from '../components/AmendmentDrawer';
import AmendmentList from '../components/AmendmentList';
import ReissueDialog, { type ReissueField } from '../components/ReissueDialog';
import {
  canAmendConsultation, canVoidPrescription, activePrescriptions,
  canSignReferral, canVoidReferral, canAddReferral, canDeleteReferral,
} from '../hooks/correctionAccess';
import { useDelegationDoctorIds } from '../hooks/useConsentedDoctorIds';
import { correctionErrorMessage } from '../hooks/correctionErrors';
import { referralReissueFields, referralReissueInitialValues, toReferralReissueRequest } from '../hooks/referralReissue';
import { useAuth } from '../../../contexts/AuthContext';
import { clinicalAmendmentService, type ClinicalAmendment } from '../../../services/clinicalAmendmentService';

const PRESCRIPTION_REISSUE_FIELDS: ReissueField[] = [
  { name: 'medication_name', label: 'Medicamento', required: true },
  { name: 'dosage', label: 'Dosis', required: true },
  { name: 'frequency', label: 'Frecuencia', required: true },
  { name: 'duration', label: 'Duración', required: true },
  { name: 'instructions', label: 'Instrucciones', multiline: true },
];

interface Specialty {
  id: number;
  name: string;
}

const ConsultationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { consultation, loading, error, fetchConsultation } = useConsultationDetail();

  const [diagnoses, setDiagnoses] = useState<ConsultationDiagnosis[]>([]);
  const [diagSearch, setDiagSearch] = useState('');
  const [diagResults, setDiagResults] = useState<Diagnosis[]>([]);
  const [diagType, setDiagType] = useState('principal');
  const [diagSearching, setDiagSearching] = useState(false);
  const [diagSaving, setDiagSaving] = useState(false);
  const diagTimeout = useRef<ReturnType<typeof setTimeout>>();

  const [prescriptions, setPrescriptions] = useState<ConsultationPrescription[]>([]);
  const [newRx, setNewRx] = useState({ medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' });
  const [showNewRx, setShowNewRx] = useState(false);
  const [rxSaving, setRxSaving] = useState(false);
  const [editingRx, setEditingRx] = useState<number | null>(null);
  const [editRxData, setEditRxData] = useState({ medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' });

  const [referrals, setReferrals] = useState<MedicalReferral[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [referredDoctors, setReferredDoctors] = useState<Array<{ id: number; first_name: string; last_name: string }>>([]);
  const [showNewReferral, setShowNewReferral] = useState(false);
  const [newReferral, setNewReferral] = useState({ referred_specialty_id: '', referred_doctor_id: '', reason: '', clinical_summary: '', priority: 'routine' });
  const [referralSaving, setReferralSaving] = useState(false);

  const [imagingOrders, setImagingOrders] = useState<ConsultationImagingOrder[]>([]);
  const [imagingStudies, setImagingStudies] = useState<ImagingStudy[]>([]);
  const [showNewImaging, setShowNewImaging] = useState(false);
  const [newImaging, setNewImaging] = useState({ imaging_study_id: '', clinical_indication: '', priority: 'routine' });
  const [imagingSaving, setImagingSaving] = useState(false);

  const [labOrders, setLabOrders] = useState<ConsultationLabOrder[]>([]);
  const [showNewLab, setShowNewLab] = useState(false);
  const [newLab, setNewLab] = useState({ test_name: '', urgency: 'routine', instructions: '' });
  const [labSaving, setLabSaving] = useState(false);
  const [labSearch, setLabSearch] = useState('');
  const [labResults, setLabResults] = useState<LabTest[]>([]);
  const labSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const [patientHistories, setPatientHistories] = useState<PatientMedicalHistory[]>([]);
  const [showHistories, setShowHistories] = useState(false);
  const [loadingHistories, setLoadingHistories] = useState(false);

  const [actionError, setActionError] = useState<string | null>(null);

  const [pendingDiagnosisDelete, setPendingDiagnosisDelete] = useState<ConsultationDiagnosis | null>(null);
  const [deletingDiagnosis, setDeletingDiagnosis] = useState(false);
  const [pendingPrescriptionDelete, setPendingPrescriptionDelete] = useState<ConsultationPrescription | null>(null);
  const [deletingPrescription, setDeletingPrescription] = useState(false);
  const [pendingReferralDelete, setPendingReferralDelete] = useState<MedicalReferral | null>(null);
  const [deletingReferral, setDeletingReferral] = useState(false);
  const [pendingImagingOrderDelete, setPendingImagingOrderDelete] = useState<ConsultationImagingOrder | null>(null);
  const [deletingImagingOrder, setDeletingImagingOrder] = useState(false);
  const [pendingLabOrderDelete, setPendingLabOrderDelete] = useState<ConsultationLabOrder | null>(null);
  const [deletingLabOrder, setDeletingLabOrder] = useState(false);

  // Vital signs edit
  const [editingVitals, setEditingVitals] = useState(false);
  const [vitalForm, setVitalForm] = useState({
    heart_rate: '', respiratory_rate: '', systolic_pressure: '', diastolic_pressure: '',
    temperature: '', height: '', weight: '', oxygen_saturation: '',
  });
  const [vitalSaving, setVitalSaving] = useState(false);

  // Lab/imaging result edit
  const [editingLabResult, setEditingLabResult] = useState<number | null>(null);
  const [labResultForm, setLabResultForm] = useState({ result: '', result_date: '' });
  const [editingImagingResult, setEditingImagingResult] = useState<number | null>(null);
  const [imagingResultForm, setImagingResultForm] = useState({ observations: '' });

  const consultationId = Number(id);
  const { actingDoctorIds, consentedDoctorIds, delegatedSignerDoctorIds } = useDelegationDoctorIds();
  const { canSign, signing, sign } = useCanSign(consultation, () => fetchConsultation(consultationId), delegatedSignerDoctorIds);
  const readOnly = isConsultationReadOnly(consultation);
  const canEdit = canEditConsultation(consultation);

  // Corrections of a signed record (clinical-record-amendments, task 4.7).
  const { user } = useAuth();
  const canAmend = canAmendConsultation(consultation, user, consentedDoctorIds);
  const [amendments, setAmendments] = useState<ClinicalAmendment[]>([]);
  const [showAmendmentDrawer, setShowAmendmentDrawer] = useState(false);
  const [reissuingRx, setReissuingRx] = useState<ConsultationPrescription | null>(null);
  const [reissuingReferral, setReissuingReferral] = useState<MedicalReferral | null>(null);
  const [signingReferralId, setSigningReferralId] = useState<number | null>(null);
  const [reissueDoctors, setReissueDoctors] = useState<Record<number, Array<{ id: number; first_name: string; last_name: string }>>>({});
  const loadAmendments = useCallback(() => {
    clinicalAmendmentService.getByConsultation(consultationId).then(setAmendments).catch((e) => console.error('Error loading amendments:', e));
  }, [consultationId]);

  useEffect(() => {
    if (readOnly) loadAmendments();
  }, [readOnly, loadAmendments]);

  useEffect(() => {
    if (id) fetchConsultation(Number(id));
  }, [id]);

  useEffect(() => {
    if (consultation) {
      setDiagnoses(consultation.diagnoses || []);
      setPrescriptions(consultation.prescriptions || []);
    }
  }, [consultation]);

  const loadReferrals = useCallback(() => {
    medicalReferralService.getByConsultation(consultationId).then(setReferrals).catch((e) => console.error('Error loading referrals:', e));
  }, [consultationId]);

  useEffect(() => {
    if (!consultationId) return;
    loadReferrals();
    consultationImagingOrderService.getByConsultation(consultationId).then(setImagingOrders).catch((e) => console.error('Error loading imaging orders:', e));
    consultationLabOrderService.getByConsultation(consultationId).then(setLabOrders).catch((e) => console.error('Error loading lab orders:', e));
  }, [consultationId]);

  const HISTORY_CATEGORY_LABELS: Record<string, string> = {
    personal: 'Personales', familiar: 'Familiares', surgical: 'Quirúrgicos',
    allergic: 'Alérgicos', vaccination: 'Vacunas', obstetric: 'Obstétricos',
  };

  const toggleHistories = useCallback(async () => {
    if (!showHistories && consultation?.patient_id && patientHistories.length === 0) {
      setLoadingHistories(true);
      try {
        const [data, types] = await Promise.all([
          patientMedicalHistoryService.getByPatient(consultation.patient_id),
          medicalHistoryTypeService.getAll(),
        ]);
        const typesMap = new Map(types.map(t => [t.id, t]));
        const enriched = data.map(h => ({
          ...h,
          history_type: typesMap.get(h.history_type_id) || h.history_type,
        }));
        setPatientHistories(enriched);
      } catch (e) { console.error('Error loading patient histories:', e); }
      setLoadingHistories(false);
    }
    setShowHistories(prev => !prev);
  }, [showHistories, consultation?.patient_id, patientHistories.length]);

  const handleDiagSearch = (value: string) => {
    setDiagSearch(value);
    clearTimeout(diagTimeout.current);
    if (value.length < 2) { setDiagResults([]); return; }
    diagTimeout.current = setTimeout(async () => {
      setDiagSearching(true);
      try {
        const results = await diagnosisService.search(value);
        setDiagResults(results || []);
      } catch (e) { console.error('Error searching diagnoses:', e); }
      setDiagSearching(false);
    }, 300);
  };

  const addDiagnosis = useCallback(async (diagnosis: Diagnosis) => {
    setDiagSaving(true);
    setActionError(null);
    try {
      await consultationDiagnosisService.create({
        consultation_id: consultationId,
        diagnosis_id: diagnosis.id,
        diagnosis_type: diagType,
      });
      await fetchConsultation(consultationId);
      setDiagSearch('');
      setDiagResults([]);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al agregar diagnóstico');
    }
    setDiagSaving(false);
  }, [consultationId, diagType, fetchConsultation]);

  const requestDiagnosisDelete = (diagnosis: ConsultationDiagnosis) => setPendingDiagnosisDelete(diagnosis);

  const cancelDiagnosisDelete = () => {
    if (deletingDiagnosis) return;
    setPendingDiagnosisDelete(null);
  };

  const confirmDiagnosisDelete = useCallback(async () => {
    if (!pendingDiagnosisDelete) return;
    const diagId = pendingDiagnosisDelete.id;
    setDeletingDiagnosis(true);
    setActionError(null);
    try {
      await consultationDiagnosisService.delete(diagId);
      setDiagnoses(prev => prev.filter(d => d.id !== diagId));
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al eliminar diagnóstico');
    } finally {
      setDeletingDiagnosis(false);
      setPendingDiagnosisDelete(null);
    }
  }, [pendingDiagnosisDelete]);

  const addPrescription = useCallback(async () => {
    if (!newRx.medication_name || !newRx.dosage || !newRx.frequency || !newRx.duration) {
      setActionError('Medicamento, dosis, frecuencia y duración son requeridos');
      return;
    }
    setRxSaving(true);
    setActionError(null);
    try {
      await consultationPrescriptionService.create({
        consultation_id: consultationId,
        ...newRx,
      });
      await fetchConsultation(consultationId);
      setNewRx({ medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' });
      setShowNewRx(false);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al agregar receta');
    }
    setRxSaving(false);
  }, [consultationId, newRx, fetchConsultation]);

  const startEditRx = (p: ConsultationPrescription) => {
    setEditingRx(p.id);
    setEditRxData({
      medication_name: p.medication_name,
      dosage: p.dosage,
      frequency: p.frequency,
      duration: p.duration,
      instructions: p.instructions || '',
    });
  };

  const saveEditRx = useCallback(async () => {
    if (!editingRx) return;
    setRxSaving(true);
    setActionError(null);
    try {
      await consultationPrescriptionService.update(editingRx, editRxData);
      await fetchConsultation(consultationId);
      setEditingRx(null);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al actualizar receta');
    }
    setRxSaving(false);
  }, [editingRx, editRxData, consultationId, fetchConsultation]);

  const requestPrescriptionDelete = (prescription: ConsultationPrescription) => setPendingPrescriptionDelete(prescription);

  const cancelPrescriptionDelete = () => {
    if (deletingPrescription) return;
    setPendingPrescriptionDelete(null);
  };

  const confirmPrescriptionDelete = useCallback(async () => {
    if (!pendingPrescriptionDelete) return;
    const rxId = pendingPrescriptionDelete.id;
    setDeletingPrescription(true);
    setActionError(null);
    try {
      await consultationPrescriptionService.delete(rxId);
      setPrescriptions(prev => prev.filter(p => p.id !== rxId));
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al eliminar receta');
    } finally {
      setDeletingPrescription(false);
      setPendingPrescriptionDelete(null);
    }
  }, [pendingPrescriptionDelete]);

  const loadSpecialties = useCallback(async () => {
    if (specialties.length > 0) return;
    try {
      const response = await apiClient.get<{ data: Specialty[] }>('/settings/specialties');
      setSpecialties(response.data.data || []);
    } catch (e) { console.error('Error loading specialties:', e); }
  }, [specialties.length]);

  const loadDoctorsBySpecialty = useCallback(async (specialtyId: number) => {
    try {
      const doctors = await doctorService.getBySpecialty(specialtyId);
      setReferredDoctors(doctors || []);
    } catch {
      setReferredDoctors([]);
    }
  }, []);

  const addReferral = useCallback(async () => {
    if (!newReferral.referred_specialty_id || !newReferral.reason) {
      setActionError('Especialidad y motivo del referimiento son requeridos');
      return;
    }
    if (!consultation) return;
    setReferralSaving(true);
    setActionError(null);
    try {
      const created = await medicalReferralService.create({
        consultation_id: consultationId,
        referring_doctor_id: consultation.doctor_id,
        referred_specialty_id: Number(newReferral.referred_specialty_id),
        referred_doctor_id: newReferral.referred_doctor_id ? Number(newReferral.referred_doctor_id) : undefined,
        reason: newReferral.reason,
        clinical_summary: newReferral.clinical_summary || undefined,
        priority: newReferral.priority,
      });
      setReferrals(prev => [created, ...prev]);
      setNewReferral({ referred_specialty_id: '', referred_doctor_id: '', reason: '', clinical_summary: '', priority: 'routine' });
      setShowNewReferral(false);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al crear referimiento');
    }
    setReferralSaving(false);
  }, [consultationId, consultation, newReferral]);

  const requestReferralDelete = (referral: MedicalReferral) => setPendingReferralDelete(referral);

  // Standalone referrals (added after the consultation was signed) are
  // signed on their own (R7). The list is reloaded either way, so a stale
  // lock_version is replaced by the current one.
  const signReferral = useCallback(async (referral: MedicalReferral) => {
    setSigningReferralId(referral.id);
    setActionError(null);
    try {
      await medicalReferralService.sign(referral.id, referral.lock_version ?? 0);
    } catch (err) {
      setActionError(correctionErrorMessage(err, 'el referimiento'));
    } finally {
      setSigningReferralId(null);
      loadReferrals();
    }
  }, [loadReferrals]);

  // Doctors per specialty for the re-issue dialog's dependent doctor select.
  const loadReissueDoctors = useCallback((specialtyId: number) => {
    if (!specialtyId || reissueDoctors[specialtyId]) return;
    doctorService.getBySpecialty(specialtyId)
      .then((doctors) => setReissueDoctors((prev) => ({ ...prev, [specialtyId]: doctors || [] })))
      .catch(() => setReissueDoctors((prev) => ({ ...prev, [specialtyId]: [] })));
  }, [reissueDoctors]);

  const openReferralReissue = (referral: MedicalReferral) => {
    loadSpecialties();
    loadReissueDoctors(referral.referred_specialty_id);
    setReissuingReferral(referral);
  };

  const cancelReferralDelete = () => {
    if (deletingReferral) return;
    setPendingReferralDelete(null);
  };

  const confirmReferralDelete = useCallback(async () => {
    if (!pendingReferralDelete) return;
    const refId = pendingReferralDelete.id;
    setDeletingReferral(true);
    setActionError(null);
    try {
      await medicalReferralService.delete(refId);
      setReferrals(prev => prev.filter(r => r.id !== refId));
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al eliminar referimiento');
    } finally {
      setDeletingReferral(false);
      setPendingReferralDelete(null);
    }
  }, [pendingReferralDelete]);

  const loadImagingStudies = useCallback(async () => {
    if (imagingStudies.length > 0) return;
    try {
      const response = await imagingStudyService.getAll({ per_page: 100, is_active: true });
      setImagingStudies(response.data || []);
    } catch (e) { console.error('Error loading imaging studies:', e); }
  }, [imagingStudies.length]);

  const addImagingOrder = useCallback(async () => {
    if (!newImaging.imaging_study_id) {
      setActionError('Seleccione un estudio de imagen');
      return;
    }
    setImagingSaving(true);
    setActionError(null);
    try {
      const created = await consultationImagingOrderService.create({
        consultation_id: consultationId,
        imaging_study_id: Number(newImaging.imaging_study_id),
        clinical_indication: newImaging.clinical_indication || undefined,
        priority: newImaging.priority,
      });
      setImagingOrders(prev => [created, ...prev]);
      setNewImaging({ imaging_study_id: '', clinical_indication: '', priority: 'routine' });
      setShowNewImaging(false);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al crear orden de imagen');
    }
    setImagingSaving(false);
  }, [consultationId, newImaging]);

  const requestImagingOrderDelete = (order: ConsultationImagingOrder) => setPendingImagingOrderDelete(order);

  const cancelImagingOrderDelete = () => {
    if (deletingImagingOrder) return;
    setPendingImagingOrderDelete(null);
  };

  const confirmImagingOrderDelete = useCallback(async () => {
    if (!pendingImagingOrderDelete) return;
    const orderId = pendingImagingOrderDelete.id;
    setDeletingImagingOrder(true);
    setActionError(null);
    try {
      await consultationImagingOrderService.delete(orderId);
      setImagingOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al eliminar orden de imagen');
    } finally {
      setDeletingImagingOrder(false);
      setPendingImagingOrderDelete(null);
    }
  }, [pendingImagingOrderDelete]);

  const handleLabSearch = (value: string) => {
    setLabSearch(value);
    setNewLab(p => ({ ...p, test_name: value }));
    clearTimeout(labSearchTimeout.current);
    if (value.length < 2) { setLabResults([]); return; }
    labSearchTimeout.current = setTimeout(async () => {
      try {
        const results = await labTestService.search(value);
        setLabResults(results || []);
      } catch (e) { console.error('Error searching lab tests:', e); }
    }, 300);
  };

  const selectLabTest = (test: LabTest) => {
    setNewLab(p => ({ ...p, test_name: test.name }));
    setLabSearch(test.name);
    setLabResults([]);
  };

  const addLabOrder = useCallback(async () => {
    if (!newLab.test_name.trim()) {
      setActionError('Ingrese el nombre del examen');
      return;
    }
    setLabSaving(true);
    setActionError(null);
    try {
      const created = await consultationLabOrderService.create({
        consultation_id: consultationId,
        test_name: newLab.test_name,
        instructions: newLab.instructions || undefined,
        urgency: newLab.urgency,
      });
      setLabOrders(prev => [created, ...prev]);
      setNewLab({ test_name: '', urgency: 'routine', instructions: '' });
      setLabSearch('');
      setShowNewLab(false);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al crear orden de laboratorio');
    }
    setLabSaving(false);
  }, [consultationId, newLab]);

  const requestLabOrderDelete = (order: ConsultationLabOrder) => setPendingLabOrderDelete(order);

  const cancelLabOrderDelete = () => {
    if (deletingLabOrder) return;
    setPendingLabOrderDelete(null);
  };

  const confirmLabOrderDelete = useCallback(async () => {
    if (!pendingLabOrderDelete) return;
    const orderId = pendingLabOrderDelete.id;
    setDeletingLabOrder(true);
    setActionError(null);
    try {
      await consultationLabOrderService.delete(orderId);
      setLabOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al eliminar orden de laboratorio');
    } finally {
      setDeletingLabOrder(false);
      setPendingLabOrderDelete(null);
    }
  }, [pendingLabOrderDelete]);

  const startEditVitals = () => {
    const vs = c?.vital_signs;
    setVitalForm({
      heart_rate: vs?.heart_rate != null ? String(vs.heart_rate) : '',
      respiratory_rate: vs?.respiratory_rate != null ? String(vs.respiratory_rate) : '',
      systolic_pressure: vs?.systolic_pressure != null ? String(vs.systolic_pressure) : '',
      diastolic_pressure: vs?.diastolic_pressure != null ? String(vs.diastolic_pressure) : '',
      temperature: vs?.temperature != null ? String(vs.temperature) : '',
      height: vs?.height != null ? String(vs.height) : '',
      weight: vs?.weight != null ? String(vs.weight) : '',
      oxygen_saturation: vs?.oxygen_saturation != null ? String(vs.oxygen_saturation) : '',
    });
    setEditingVitals(true);
  };

  const saveVitals = useCallback(async () => {
    setVitalSaving(true);
    setActionError(null);
    const payload: Record<string, number> = {};
    Object.entries(vitalForm).forEach(([k, v]) => {
      if (v !== '') payload[k] = Number(v);
    });
    try {
      const vs = consultation?.vital_signs;
      if (vs?.id) {
        await apiClient.put(`/vital-signs/${vs.id}`, payload);
      } else {
        await apiClient.post('/vital-signs', { consultation_id: consultationId, ...payload });
      }
      await fetchConsultation(consultationId);
      setEditingVitals(false);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al guardar signos vitales');
    }
    setVitalSaving(false);
  }, [vitalForm, consultation, consultationId, fetchConsultation]);

  const saveLabResult = useCallback(async (orderId: number) => {
    try {
      const updated = await consultationLabOrderService.update(orderId, {
        result: labResultForm.result || undefined,
        result_date: labResultForm.result_date || undefined,
      });
      setLabOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updated } : o));
      setEditingLabResult(null);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al guardar resultado');
    }
  }, [labResultForm]);

  const saveImagingResult = useCallback(async (orderId: number) => {
    try {
      const updated = await consultationImagingOrderService.update(orderId, {
        observations: imagingResultForm.observations || undefined,
      });
      setImagingOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updated } : o));
      setEditingImagingResult(null);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Error al guardar resultado');
    }
  }, [imagingResultForm]);


  const priorityLabel = (p: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      routine: { label: 'Rutina', cls: 'custom-badge status-purple' },
      urgent: { label: 'Urgente', cls: 'custom-badge status-orange' },
      emergency: { label: 'Emergencia', cls: 'custom-badge status-red' },
      stat: { label: 'STAT', cls: 'custom-badge status-red' },
    };
    const m = map[p] || map.routine;
    return <span className={m.cls}>{m.label}</span>;
  };

  const statusLabel = (s: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      pending: { label: 'Pendiente', cls: 'custom-badge status-orange' },
      accepted: { label: 'Aceptado', cls: 'custom-badge status-blue' },
      completed: { label: 'Completado', cls: 'custom-badge status-green' },
      cancelled: { label: 'Cancelado', cls: 'custom-badge status-grey' },
    };
    const m = map[s] || map.pending;
    return <span className={m.cls}>{m.label}</span>;
  };

  // Full-page spinner only while there is nothing of THIS consultation to
  // show yet. A refetch of the already-loaded record (after signing, an
  // amendment, a re-issue) must not unmount the page: that would also
  // unmount the open AmendmentDrawer / ReissueDialog and silently drop
  // their in-flight state and error messages (PR 4c re-review CRITICAL).
  if (loading && consultation?.id !== consultationId) {
    return <Spinner variant="full-page" text="Cargando consulta..." size="lg" />;
  }

  if (error) {
    return (
      <>
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <Breadcrumb items={[{ label: 'Consultas', to: '/consultations' }, { label: 'Error' }]} />
            </div>
          </div>
        </div>
        <div className="alert alert-danger">{error}</div>
        <Link to="/consultations" className="btn btn-primary">
          <i className="fa fa-arrow-left me-1"></i> Volver a Consultas
        </Link>
      </>
    );
  }

  if (!consultation) return null;
  const c = consultation;
  const pediatricData: any = c.specialty_data ? (c.specialty_data as any).pediatric : null;
  const internalMedicineData: any = c.specialty_data ? (c.specialty_data as any).internal_medicine : null;
  const traumatologiaData: any = c.specialty_data ? (c.specialty_data as any).traumatologia : null;
  const dermatologiaData: any = c.specialty_data ? (c.specialty_data as any).dermatologia : null;
  const oftalmologiaData: any = c.specialty_data ? (c.specialty_data as any).oftalmologia : null;

  const pedPercentileBadge = (pct: number | null) => {
    if (pct === null) return null;
    let color = '#6b7280', bg = '#f3f4f6', label = 'Sin datos';
    if (pct < 3) { color = '#ef4444'; bg = '#fef2f2'; label = `<3 · Bajo`; }
    else if (pct < 15) { color = '#f59e0b'; bg = '#fffbeb'; label = `P${pct} · Límite`; }
    else if (pct < 85) { color = '#10b981'; bg = '#f0fdf4'; label = `P${pct} · Normal`; }
    else if (pct < 97) { color = '#f59e0b'; bg = '#fffbeb'; label = `P${pct} · Elevado`; }
    else { color = '#ef4444'; bg = '#fef2f2'; label = `>97 · Alto`; }
    return (
      <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, color, background: bg, border: `1px solid ${color}40`, marginLeft: 8 }}>
        {label}
      </span>
    );
  };

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <div className="row">
          <div className="col-sm-12">
            <Breadcrumb items={[{ label: 'Consultas', to: '/consultations' }, { label: 'Detalle de Consulta' }]} />
          </div>
        </div>
      </div>

      {actionError && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="btn-close" onClick={() => setActionError(null)}></button>
          {actionError}
        </div>
      )}

      {/* Header con fecha y botón volver */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title mb-1">
                    Consulta Médica <ConsultationStatusBadge status={c.status} signatureMethod={c.signature_method} />
                  </h4>
                  <p className="text-muted mb-0">
                    <i className="fa fa-calendar me-1"></i> {formatDate(c.consultation_date)}
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => window.open(`/consultations/${c.id}/print`, '_blank')}
                    title="Abrir nota clínica en nueva pestaña para imprimir"
                  >
                    <i className="fa fa-print me-1"></i> Nota Clínica
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={async () => {
                      if (!readOnly) {
                        setActionError('El documento está pendiente de firma y no puede imprimirse.');
                        return;
                      }
                      try { await consultationService.downloadPdf(c.id); }
                      catch { setActionError('Error al generar el PDF'); }
                    }}
                    title="Descargar PDF generado por el servidor"
                  >
                    <i className="fa fa-file-pdf me-1"></i> PDF
                  </button>
                  {canSign && (
                    <button
                      className="btn btn-success btn-sm"
                      disabled={signing}
                      onClick={() => sign().catch((err) => setActionError(signErrorMessage(err)))}
                      title="Firmar consulta"
                    >
                      <i className="fa fa-signature me-1"></i> {signing ? 'Firmando...' : 'Firmar consulta'}
                    </button>
                  )}
                  {canAmend && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setShowAmendmentDrawer(true)} title="Corregir la consulta firmada mediante una enmienda">
                      <i className="fa fa-file-signature me-1"></i> Enmendar
                    </button>
                  )}
                  {canEdit && (
                    <Link to={`/consultations/${c.id}/edit`} className="btn btn-outline-secondary btn-sm">
                      <i className="fa fa-edit me-1"></i> Editar
                    </Link>
                  )}
                  <Link to="/consultations" className="btn btn-primary btn-sm">
                    <i className="fa fa-arrow-left me-1"></i> Volver
                  </Link>
                  <Link
                    to={`/billing/new?consultation_id=${c.id}`}
                    className="btn btn-primary btn-sm"
                  >
                    <i className="fa fa-file-lines me-1"></i> Generar Factura
                  </Link>
                  <Link
                    to={`/appointments/new?patient_id=${c.patient_id}&doctor_id=${c.doctor_id}`}
                    className="btn btn-primary btn-sm"
                  >
                    <i className="fa fa-calendar-plus me-1"></i> Crear Cita de Control
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enmiendas: the original above stays unchanged (R2) */}
      {amendments.length > 0 && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title mb-3"><i className="fa fa-file-signature me-2"></i>Enmiendas</h4>
                <AmendmentList amendments={amendments} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Paciente y Doctor */}
      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><i className="fa fa-user me-2"></i>Paciente</h4>
              {c.patient ? (
                <div className="mt-3">
                  <p className="mb-1"><strong>Nombre:</strong> {c.patient.first_name} {c.patient.last_name}</p>
                  <p className="mb-1"><strong>Identificación:</strong> {c.patient.identification}</p>
                  <Link to={`/patients/${c.patient.id}`} className="btn btn-outline-primary btn-sm mt-2">Ver perfil</Link>
                </div>
              ) : <p className="text-muted mt-3">Paciente no disponible</p>}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><i className="fa fa-user-md me-2"></i>Doctor</h4>
              {c.doctor ? (
                <div className="mt-3">
                  <p className="mb-1"><strong>Nombre:</strong> Dr. {c.doctor.first_name} {c.doctor.last_name}</p>
                  {c.doctor.specialty && <p className="mb-1"><strong>Especialidad:</strong> {c.doctor.specialty.name}</p>}
                </div>
              ) : <p className="text-muted mt-3">Doctor no disponible</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Antecedentes del Paciente */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }} onClick={toggleHistories}>
                <h4 className="card-title mb-0">
                  <i className={`fa fa-${showHistories ? 'chevron-down' : 'chevron-right'} me-2`}></i>
                  <i className="fa fa-history me-2"></i>Antecedentes del Paciente
                  {patientHistories.length > 0 && (
                    <span className="badge bg-purple" style={{ marginLeft: '8px' }}>{patientHistories.length}</span>
                  )}
                </h4>
                <small className="text-muted">{showHistories ? 'Ocultar' : 'Ver antecedentes'}</small>
              </div>

              {showHistories && (
                <div className="mt-3">
                  {loadingHistories ? (
                    <div className="text-center" style={{ padding: '20px' }}>
                      <Spinner text="Cargando antecedentes..." />
                    </div>
                  ) : patientHistories.length === 0 ? (
                    <EmptyState
                      icon="fa-file-lines"
                      title="No tiene antecedentes registrados"
                      action={c.patient ? { label: 'Agregar desde el perfil', to: `/patients/${c.patient.id}`, icon: 'fa-plus' } : undefined}
                    />
                  ) : (
                    <div className="row">
                      {Object.entries(
                        patientHistories.reduce((acc, h) => {
                          const cat = h.history_type?.category || 'personal';
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(h);
                          return acc;
                        }, {} as Record<string, PatientMedicalHistory[]>)
                      ).map(([category, items]) => (
                        <div key={category} className="col-md-6" style={{ marginBottom: '15px' }}>
                          <h6 style={{ borderBottom: '1px solid #eee', paddingBottom: '5px', color: '#333' }}>
                            {HISTORY_CATEGORY_LABELS[category] || category}
                            <span className="badge bg-secondary" style={{ marginLeft: '5px', fontSize: '10px' }}>{items.length}</span>
                          </h6>
                          <ul className="list-unstyled" style={{ marginLeft: '5px' }}>
                            {items.map(h => (
                              <li key={h.id} style={{ marginBottom: '6px', fontSize: '13px' }}>
                                <strong>{h.history_type?.name || '-'}:</strong> {h.description}
                                {h.onset_date && <small className="text-muted"> (desde {formatDate(h.onset_date)})</small>}
                                {h.notes && <small className="text-muted d-block" style={{ marginLeft: '10px' }}>{h.notes}</small>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Datos Clínicos */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><i className="fa fa-stethoscope me-2"></i>Datos Clínicos</h4>
              <div className="row mt-3">
                <div className="col-md-6">
                  <h6 className="text-muted">Motivo de Consulta</h6>
                  <p>{c.chief_complaint}</p>
                </div>
                <div className="col-md-6">
                  <h6 className="text-muted">Enfermedad Actual</h6>
                  <p>{c.present_illness || '-'}</p>
                </div>
              </div>
              {c.physical_examination && (
                <div className="mt-2">
                  <h6 className="text-muted">Examen Físico</h6>
                  <p>{c.physical_examination}</p>
                </div>
              )}
              {c.general_recommendations && (
                <div className="mt-2">
                  <h6 className="text-muted">Recomendaciones</h6>
                  <p>{c.general_recommendations}</p>
                </div>
              )}
              {c.follow_up_date && (
                <div className="mt-2">
                  <h6 className="text-muted">Fecha de Control</h6>
                  <p>
                    <i className="fa fa-calendar me-1 text-primary"></i>
                    {formatDate(c.follow_up_date, { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SOAP - Nota Estructurada */}
      {(c.subjective || c.objective || c.assessment || c.plan) && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title"><i className="fa fa-clipboard me-2"></i>SOAP - Nota Estructurada</h4>
                <div className="row mt-3">
                  {c.subjective && (
                    <div className="col-md-6">
                      <h6 className="text-muted">Subjetivo (S)</h6>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{c.subjective}</p>
                    </div>
                  )}
                  {c.objective && (
                    <div className="col-md-6">
                      <h6 className="text-muted">Objetivo (O)</h6>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{c.objective}</p>
                    </div>
                  )}
                </div>
                <div className="row">
                  {c.assessment && (
                    <div className="col-md-6">
                      <h6 className="text-muted">Evaluación (A)</h6>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{c.assessment}</p>
                    </div>
                  )}
                  {c.plan && (
                    <div className="col-md-6">
                      <h6 className="text-muted">Plan (P)</h6>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{c.plan}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revisión por Sistemas */}
      {c.review_of_systems && c.review_of_systems.length > 0 && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title"><i className="fa fa-list-ul me-2"></i>Revisión por Sistemas</h4>
                <div className="row mt-3">
                  {c.review_of_systems
                    .filter((ros: any) => ros.has_findings)
                    .map((ros: any) => (
                      <div key={ros.system_code} className="col-md-6 mb-2">
                        <div className="d-flex align-items-start">
                          <div className="me-2 mt-1">
                            <i className="fa fa-exclamation-circle text-warning"></i>
                          </div>
                          <div>
                            <strong>{ros.system_code}</strong>
                            {ros.findings_description && (
                              <p className="mb-0 text-muted small" style={{ whiteSpace: 'pre-wrap' }}>
                                {ros.findings_description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  {c.review_of_systems.filter((ros: any) => ros.has_findings).length === 0 && (
                    <div className="col-12">
                      <p className="text-muted">Sin hallazgos positivos en la revisión por sistemas.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signos Vitales */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><i className="fa fa-heartbeat me-2"></i>Signos Vitales</h4>
                {!editingVitals && canEdit && (
                  <button className="btn btn-outline-primary btn-sm" onClick={startEditVitals}>
                    <i className="fa fa-edit me-1"></i> {c.vital_signs ? 'Editar' : 'Registrar'}
                  </button>
                )}
              </div>

              {editingVitals ? (
                <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 12 }}>
                  <div className="row">
                    {([
                      { key: 'heart_rate', label: 'FC (lpm)', type: 'number' },
                      { key: 'respiratory_rate', label: 'FR (rpm)', type: 'number' },
                      { key: 'systolic_pressure', label: 'PA Sistólica', type: 'number' },
                      { key: 'diastolic_pressure', label: 'PA Diastólica', type: 'number' },
                      { key: 'temperature', label: 'Temperatura (°C)', type: 'number' },
                      { key: 'height', label: 'Talla (cm)', type: 'number' },
                      { key: 'weight', label: 'Peso (kg)', type: 'number' },
                      { key: 'oxygen_saturation', label: 'SpO2 (%)', type: 'number' },
                    ] as const).map(({ key, label }) => (
                      <div className="col-md-3 col-6 mb-3" key={key}>
                        <label className="form-label" style={{ fontSize: 12 }}>{label}</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={vitalForm[key]}
                          onChange={(e) => setVitalForm(prev => ({ ...prev, [key]: e.target.value }))}
                          step="any"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="d-flex gap-2 mt-2">
                    <button className="btn btn-primary btn-sm" onClick={saveVitals} disabled={vitalSaving}>
                      {vitalSaving ? <Spinner size="sm" text="Guardando..." /> : <><i className="fa fa-check me-1"></i> Guardar</>}
                    </button>
                    <button className="btn btn-light btn-sm" onClick={() => setEditingVitals(false)}>Cancelar</button>
                  </div>
                </div>
              ) : c.vital_signs ? (
                <div className="row mt-2">
                  {c.vital_signs.heart_rate != null && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.heart_rate}</h3>
                        <small className="text-muted">FC (lpm)</small>
                      </div>
                    </div>
                  )}
                  {c.vital_signs.respiratory_rate != null && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.respiratory_rate}</h3>
                        <small className="text-muted">FR (rpm)</small>
                      </div>
                    </div>
                  )}
                  {(c.vital_signs.systolic_pressure != null || c.vital_signs.diastolic_pressure != null) && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.systolic_pressure || '-'}/{c.vital_signs.diastolic_pressure || '-'}</h3>
                        <small className="text-muted">PA (mmHg)</small>
                      </div>
                    </div>
                  )}
                  {c.vital_signs.temperature != null && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.temperature}°C</h3>
                        <small className="text-muted">Temperatura</small>
                      </div>
                    </div>
                  )}
                  {c.vital_signs.weight != null && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.weight} kg</h3>
                        <small className="text-muted">Peso</small>
                      </div>
                    </div>
                  )}
                  {c.vital_signs.height != null && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.height} cm</h3>
                        <small className="text-muted">Talla</small>
                      </div>
                    </div>
                  )}
                  {c.vital_signs.bmi != null && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.bmi}</h3>
                        <small className="text-muted">IMC</small>
                      </div>
                    </div>
                  )}
                  {c.vital_signs.oxygen_saturation != null && (
                    <div className="col-md-3 col-6">
                      <div className="card text-center" style={{ padding: 15, marginBottom: 10 }}>
                        <h3 className="mb-0">{c.vital_signs.oxygen_saturation}%</h3>
                        <small className="text-muted">SpO2</small>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState icon="fa-heartbeat" title="No hay signos vitales registrados" className="py-3" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Diagnósticos */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title"><i className="fa fa-medkit me-2"></i>Diagnósticos</h4>
              {canEdit && (
              <div className="row mt-3 mb-3">
                <div className="col-md-6" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar diagnóstico CIE-10..."
                    value={diagSearch}
                    onChange={(e) => handleDiagSearch(e.target.value)}
                  />
                  {diagSearching && <small className="text-muted"><Spinner text="Buscando..." /></small>}
                  {diagResults.length > 0 && (
                    <ul className="list-group" style={{ position: 'absolute', zIndex: 10, width: '95%', maxHeight: 200, overflowY: 'auto' }}>
                      {diagResults.map((d) => (
                        <li key={d.id} className="list-group-item list-group-item-action" style={{ cursor: 'pointer' }}
                          onClick={() => addDiagnosis(d)}>
                          <strong>{d.code}</strong> — {d.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="col-md-3">
                  <select className="form-control" value={diagType} onChange={(e) => setDiagType(e.target.value)}>
                    <option value="principal">Principal</option>
                    <option value="secundario">Secundario</option>
                    <option value="presuntivo">Presuntivo</option>
                    <option value="definitivo">Definitivo</option>
                  </select>
                </div>
                {diagSaving && <div className="col-md-3 d-flex align-items-center"><Spinner text="Guardando..." /></div>}
              </div>
              )}

              {diagnoses.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th>Tipo</th>
                        <th>Observaciones</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnoses.map((d) => (
                        <tr key={d.id}>
                          <td><strong>{d.diagnosis?.code || '-'}</strong></td>
                          <td>{d.diagnosis?.description || '-'}</td>
                          <td>
                            <span className={`custom-badge ${(d.type || d.diagnosis_type) === 'principal' ? 'status-blue' : (d.type || d.diagnosis_type) === 'secundario' ? 'status-purple' : 'status-orange'}`}>
                              {d.type || d.diagnosis_type}
                            </span>
                          </td>
                          <td>{d.observations || '-'}</td>
                          <td>
                            {canEdit && (
                              <button className="btn btn-sm bg-danger-light" onClick={() => requestDiagnosisDelete(d)} title="Eliminar">
                                <i className="fa fa-times"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="fa-medkit" title="No hay diagnósticos registrados" description="Use el buscador para agregar diagnósticos CIE-10" className="py-3" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recetas */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><i className="fa fa-file-lines me-2"></i>Receta Médica</h4>
                <div>
                  {activePrescriptions(prescriptions).length > 0 && (
                    <button
                      className="btn btn-outline-secondary btn-sm me-2"
                      onClick={() => window.open(`/consultations/${id}/prescription`, '_blank')}
                      title="Imprimir receta en nueva pestaña"
                    >
                      <i className="fa fa-print me-1"></i> Imprimir Receta
                    </button>
                  )}
                  {activePrescriptions(prescriptions).length > 0 && (
                    <button
                      className="btn btn-outline-info btn-sm me-2"
                      onClick={async () => {
                        if (!readOnly) {
                          setActionError('El documento está pendiente de firma y no puede imprimirse.');
                          return;
                        }
                        try {
                          await consultationPrescriptionService.downloadPdf(Number(id));
                        } catch {
                          setActionError('Error al generar el PDF');
                        }
                      }}
                    >
                      <i className="fa fa-print me-1"></i> Imprimir
                    </button>
                  )}
                  {canEdit && (
                    <button className="btn btn-primary btn-sm" onClick={() => setShowNewRx(true)}>
                      <i className="fa fa-plus me-1"></i> Agregar
                    </button>
                  )}
                </div>
              </div>

              {showNewRx && (
                <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 12, marginBottom: 20 }}>
                  <h6 className="mb-3">Nuevo Medicamento</h6>
                  <div className="row">
                    <div className="col-md-3">
                      <input type="text" className="form-control" placeholder="Medicamento *"
                        value={newRx.medication_name} onChange={(e) => setNewRx(p => ({ ...p, medication_name: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" placeholder="Dosis *"
                        value={newRx.dosage} onChange={(e) => setNewRx(p => ({ ...p, dosage: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" placeholder="Frecuencia *"
                        value={newRx.frequency} onChange={(e) => setNewRx(p => ({ ...p, frequency: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" placeholder="Duración *"
                        value={newRx.duration} onChange={(e) => setNewRx(p => ({ ...p, duration: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <input type="text" className="form-control" placeholder="Instrucciones"
                        value={newRx.instructions} onChange={(e) => setNewRx(p => ({ ...p, instructions: e.target.value }))} />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button className="btn btn-primary btn-sm me-2" onClick={addPrescription} disabled={rxSaving}>
                      {rxSaving ? <Spinner size="sm" text="Guardando..." /> : <><i className="fa fa-check me-1"></i> Guardar</>}
                    </button>
                    <button className="btn btn-light btn-sm" onClick={() => setShowNewRx(false)}>Cancelar</button>
                  </div>
                </div>
              )}

              {prescriptions.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Medicamento</th>
                        <th>Dosis</th>
                        <th>Frecuencia</th>
                        <th>Duración</th>
                        <th>Instrucciones</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {prescriptions.map((p) => (
                        editingRx === p.id ? (
                          <tr key={p.id}>
                            <td><input type="text" className="form-control form-control-sm" value={editRxData.medication_name}
                              onChange={(e) => setEditRxData(d => ({ ...d, medication_name: e.target.value }))} /></td>
                            <td><input type="text" className="form-control form-control-sm" value={editRxData.dosage}
                              onChange={(e) => setEditRxData(d => ({ ...d, dosage: e.target.value }))} /></td>
                            <td><input type="text" className="form-control form-control-sm" value={editRxData.frequency}
                              onChange={(e) => setEditRxData(d => ({ ...d, frequency: e.target.value }))} /></td>
                            <td><input type="text" className="form-control form-control-sm" value={editRxData.duration}
                              onChange={(e) => setEditRxData(d => ({ ...d, duration: e.target.value }))} /></td>
                            <td><input type="text" className="form-control form-control-sm" value={editRxData.instructions}
                              onChange={(e) => setEditRxData(d => ({ ...d, instructions: e.target.value }))} /></td>
                            <td>
                              <button className="btn btn-sm bg-success-light me-1" onClick={saveEditRx} disabled={rxSaving} title="Guardar">
                                <i className="fa fa-check"></i>
                              </button>
                              <button className="btn btn-sm bg-danger-light" onClick={() => setEditingRx(null)} title="Cancelar">
                                <i className="fa fa-times"></i>
                              </button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={p.id} style={p.voided_at ? { opacity: 0.55 } : undefined}>
                            <td>
                              {p.medication_name}
                              {p.voided_at && <span className="custom-badge status-red ms-2" title={p.void_reason || undefined}>Anulada</span>}
                            </td>
                            <td>{p.dosage}</td>
                            <td>{p.frequency}</td>
                            <td>{p.duration}</td>
                            <td>{p.instructions || '-'}</td>
                            <td>
                              {canEdit && (
                                <>
                                  <button className="btn btn-sm bg-info-light me-1" onClick={() => startEditRx(p)} title="Editar">
                                    <i className="fa fa-edit"></i>
                                  </button>
                                  <button className="btn btn-sm bg-danger-light" onClick={() => requestPrescriptionDelete(p)} title="Eliminar">
                                    <i className="fa fa-trash"></i>
                                  </button>
                                </>
                              )}
                              {canVoidPrescription(p, c, user, delegatedSignerDoctorIds) && (
                                <button className="btn btn-sm btn-outline-warning" onClick={() => setReissuingRx(p)} title="Corregir receta">
                                  <i className="fa fa-rotate me-1"></i> Corregir
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !showNewRx && <EmptyState icon="fa-file-lines" title="No hay recetas registradas" className="py-3" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Órdenes de Imagen */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><i className="fa fa-film me-2"></i>Órdenes de Imagen</h4>
                {canEdit && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setShowNewImaging(true); loadImagingStudies(); }}>
                    <i className="fa fa-plus me-1"></i> Agregar
                  </button>
                )}
              </div>

              {showNewImaging && (
                <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 12, marginBottom: 20 }}>
                  <h6 className="mb-3">Nueva Orden de Imagen</h6>
                  <div className="row">
                    <div className="col-md-4">
                      <label className="form-label">Estudio *</label>
                      <select className="form-control" value={newImaging.imaging_study_id}
                        onChange={(e) => setNewImaging(p => ({ ...p, imaging_study_id: e.target.value }))}>
                        <option value="">Seleccionar estudio...</option>
                        {imagingStudies.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.code} — {s.name} {s.department ? `(${s.department.name})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Indicación Clínica</label>
                      <input type="text" className="form-control" placeholder="Indicación clínica"
                        value={newImaging.clinical_indication} onChange={(e) => setNewImaging(p => ({ ...p, clinical_indication: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Prioridad</label>
                      <select className="form-control" value={newImaging.priority}
                        onChange={(e) => setNewImaging(p => ({ ...p, priority: e.target.value }))}>
                        <option value="routine">Rutina</option>
                        <option value="urgent">Urgente</option>
                        <option value="stat">STAT</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button className="btn btn-primary btn-sm me-2" onClick={addImagingOrder} disabled={imagingSaving}>
                      {imagingSaving ? <Spinner size="sm" text="Guardando..." /> : <><i className="fa fa-check me-1"></i> Guardar</>}
                    </button>
                    <button className="btn btn-light btn-sm" onClick={() => setShowNewImaging(false)}>Cancelar</button>
                  </div>
                </div>
              )}

              {imagingOrders.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Estudio</th>
                        <th>Departamento</th>
                        <th>Indicación</th>
                        <th>Prioridad</th>
                        <th>Resultado</th>
                        <th style={{ width: 70 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {imagingOrders.map((o) => (
                        <tr key={o.id}>
                          <td>
                            {o.imaging_study?.name || '-'}
                            {o.imaging_study?.requires_contrast && <span className="badge bg-warning ms-1">C</span>}
                          </td>
                          <td>{o.imaging_study?.department?.name || '-'}</td>
                          <td>{o.clinical_indication || '-'}</td>
                          <td>{priorityLabel(o.priority)}</td>
                          <td>
                            {editingImagingResult === o.id ? (
                              <div className="d-flex gap-1 align-items-center">
                                <textarea
                                  className="form-control form-control-sm"
                                  rows={2}
                                  style={{ minWidth: 160 }}
                                  value={imagingResultForm.observations}
                                  onChange={(e) => setImagingResultForm({ observations: e.target.value })}
                                  placeholder="Observaciones / resultado..."
                                />
                                <div className="d-flex flex-column gap-1">
                                  <button className="btn btn-xs bg-success-light" onClick={() => saveImagingResult(o.id)} title="Guardar"><i className="fa fa-check"></i></button>
                                  <button className="btn btn-xs bg-danger-light" onClick={() => setEditingImagingResult(null)} title="Cancelar"><i className="fa fa-times"></i></button>
                                </div>
                              </div>
                            ) : (
                              <span
                                style={{ cursor: 'pointer', color: (o as any).observations ? '#333' : '#aaa', fontSize: 12 }}
                                onClick={() => { setEditingImagingResult(o.id); setImagingResultForm({ observations: (o as any).observations || '' }); }}
                                title="Clic para registrar resultado"
                              >
                                {(o as any).observations || <em>Sin resultado</em>}
                              </span>
                            )}
                          </td>
                          <td>
                            {canEdit && (
                              <button className="btn btn-sm bg-danger-light" onClick={() => requestImagingOrderDelete(o)} title="Eliminar">
                                <i className="fa fa-times"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !showNewImaging && <EmptyState icon="fa-image" title="No hay órdenes de imagen" className="py-3" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Órdenes de Laboratorio */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><i className="fa fa-flask me-2"></i>Órdenes de Laboratorio</h4>
                {canEdit && (
                  <button className="btn btn-primary btn-sm" onClick={() => setShowNewLab(true)}>
                    <i className="fa fa-plus me-1"></i> Agregar
                  </button>
                )}
              </div>

              {showNewLab && (
                <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 12, marginBottom: 20 }}>
                  <h6 className="mb-3">Nueva Orden de Laboratorio</h6>
                  <div className="row">
                    <div className="col-md-5" style={{ position: 'relative' }}>
                      <label className="form-label">Examen *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar examen o escribir nombre..."
                        value={labSearch}
                        onChange={(e) => handleLabSearch(e.target.value)}
                        autoComplete="off"
                      />
                      {labResults.length > 0 && (
                        <ul className="list-group" style={{ position: 'absolute', zIndex: 10, width: '95%', maxHeight: 180, overflowY: 'auto', top: '100%' }}>
                          {labResults.map((t) => (
                            <li key={t.id} className="list-group-item list-group-item-action" style={{ cursor: 'pointer', fontSize: 13 }}
                              onClick={() => selectLabTest(t)}>
                              <strong>{t.code}</strong> — {t.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Urgencia</label>
                      <select className="form-control" value={newLab.urgency}
                        onChange={(e) => setNewLab(p => ({ ...p, urgency: e.target.value }))}>
                        <option value="routine">Rutina</option>
                        <option value="urgent">Urgente</option>
                        <option value="stat">STAT</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Instrucciones</label>
                      <input type="text" className="form-control" placeholder="Instrucciones especiales"
                        value={newLab.instructions} onChange={(e) => setNewLab(p => ({ ...p, instructions: e.target.value }))} />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button className="btn btn-primary btn-sm me-2" onClick={addLabOrder} disabled={labSaving}>
                      {labSaving ? <Spinner size="sm" text="Guardando..." /> : <><i className="fa fa-check me-1"></i> Guardar</>}
                    </button>
                    <button className="btn btn-light btn-sm" onClick={() => { setShowNewLab(false); setLabSearch(''); setLabResults([]); }}>Cancelar</button>
                  </div>
                </div>
              )}

              {labOrders.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Examen</th>
                        <th>Urgencia</th>
                        <th>Instrucciones</th>
                        <th>Resultado</th>
                        <th>Fecha Resultado</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {labOrders.map((o) => (
                        <tr key={o.id}>
                          <td>
                            {o.panel ? (
                              <span><i className="fa fa-layer-group me-1 text-muted"></i>{o.panel.name}</span>
                            ) : (
                              <span>{o.test?.name || o.test_name}</span>
                            )}
                          </td>
                          <td>{priorityLabel(o.urgency)}</td>
                          <td>{o.instructions || '-'}</td>
                          <td>
                            {editingLabResult === o.id ? (
                              <div className="d-flex gap-1 align-items-center">
                                <textarea
                                  className="form-control form-control-sm"
                                  rows={2}
                                  style={{ minWidth: 160 }}
                                  value={labResultForm.result}
                                  onChange={(e) => setLabResultForm(prev => ({ ...prev, result: e.target.value }))}
                                  placeholder="Resultado del examen..."
                                />
                                <div className="d-flex flex-column gap-1">
                                  <button className="btn btn-xs bg-success-light" onClick={() => saveLabResult(o.id)} title="Guardar"><i className="fa fa-check"></i></button>
                                  <button className="btn btn-xs bg-danger-light" onClick={() => setEditingLabResult(null)} title="Cancelar"><i className="fa fa-times"></i></button>
                                </div>
                              </div>
                            ) : (
                              <span
                                style={{ cursor: 'pointer', color: (o as any).result ? '#333' : '#aaa', fontSize: 12 }}
                                onClick={() => { setEditingLabResult(o.id); setLabResultForm({ result: (o as any).result || '', result_date: (o as any).result_date || '' }); }}
                                title="Clic para registrar resultado"
                              >
                                {(o as any).result || <em>Sin resultado</em>}
                              </span>
                            )}
                          </td>
                          <td>
                            {editingLabResult === o.id ? (
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={labResultForm.result_date}
                                onChange={(e) => setLabResultForm(prev => ({ ...prev, result_date: e.target.value }))}
                              />
                            ) : (
                              <small className="text-muted">{(o as any).result_date || '-'}</small>
                            )}
                          </td>
                          <td>
                            {canEdit && (
                              <button className="btn btn-sm bg-danger-light" onClick={() => requestLabOrderDelete(o)} title="Eliminar">
                                <i className="fa fa-times"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !showNewLab && <EmptyState icon="fa-flask" title="No hay órdenes de laboratorio" className="py-3" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Referimientos */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0"><i className="fa fa-share-from-square me-2"></i>Referimientos</h4>
                {canAddReferral(c, user, actingDoctorIds) && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setShowNewReferral(true); loadSpecialties(); }}>
                    <i className="fa fa-plus me-1"></i> Agregar
                  </button>
                )}
              </div>

              {showNewReferral && (
                <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 12, marginBottom: 20 }}>
                  <h6 className="mb-3">Nuevo Referimiento</h6>
                  <div className="row">
                    <div className="col-md-3">
                      <label className="form-label">Especialidad *</label>
                      <select className="form-control" value={newReferral.referred_specialty_id}
                        onChange={(e) => { setNewReferral(p => ({ ...p, referred_specialty_id: e.target.value, referred_doctor_id: '' })); loadDoctorsBySpecialty(Number(e.target.value)); }}>
                        <option value="">Seleccionar...</option>
                        {specialties.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Doctor</label>
                      <select className="form-control" value={newReferral.referred_doctor_id}
                        onChange={(e) => setNewReferral(p => ({ ...p, referred_doctor_id: e.target.value }))}>
                        <option value="">Cualquier doctor</option>
                        {referredDoctors.map(d => (
                          <option key={d.id} value={d.id}>Dr. {d.first_name} {d.last_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Motivo *</label>
                      <input type="text" className="form-control" placeholder="Motivo del referimiento"
                        value={newReferral.reason} onChange={(e) => setNewReferral(p => ({ ...p, reason: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Prioridad</label>
                      <select className="form-control" value={newReferral.priority}
                        onChange={(e) => setNewReferral(p => ({ ...p, priority: e.target.value }))}>
                        <option value="routine">Rutina</option>
                        <option value="urgent">Urgente</option>
                        <option value="emergency">Emergencia</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button className="btn btn-primary btn-sm me-2" onClick={addReferral} disabled={referralSaving}>
                      {referralSaving ? <Spinner size="sm" text="Guardando..." /> : <><i className="fa fa-check me-1"></i> Guardar</>}
                    </button>
                    <button className="btn btn-light btn-sm" onClick={() => setShowNewReferral(false)}>Cancelar</button>
                  </div>
                </div>
              )}

              {referrals.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Especialidad</th>
                        <th>Motivo</th>
                        <th>Resumen</th>
                        <th>Prioridad</th>
                        <th>Estado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((r) => (
                        <tr key={r.id} style={r.voided_at ? { opacity: 0.55 } : undefined}>
                          <td>
                            {r.referred_specialty?.name || '-'}
                            {r.voided_at && <span className="custom-badge status-red ms-2" title={r.void_reason || undefined}>Anulado</span>}
                            {!r.voided_at && !r.locked_with_consultation && r.signing_status === 'draft' && (
                              <span className="custom-badge status-orange ms-2" title="Agregado después de firmar la consulta: requiere su propia firma">Pendiente de firma</span>
                            )}
                            {r.referred_doctor && (
                              <small className="text-muted d-block">Dr. {r.referred_doctor.first_name} {r.referred_doctor.last_name}</small>
                            )}
                          </td>
                          <td>{r.reason}</td>
                          <td>{r.clinical_summary || '-'}</td>
                          <td>{priorityLabel(r.priority)}</td>
                          <td>{statusLabel(r.status)}</td>
                          <td className="text-nowrap">
                            {canSignReferral(r, c, user) && (
                              <button className="btn btn-sm btn-outline-success me-1" onClick={() => signReferral(r)} disabled={signingReferralId === r.id} title="Firmar referimiento">
                                <i className="fa fa-signature me-1"></i> {signingReferralId === r.id ? 'Firmando...' : 'Firmar'}
                              </button>
                            )}
                            {canVoidReferral(r, c, user, delegatedSignerDoctorIds) && (
                              <button className="btn btn-sm btn-outline-warning me-1" onClick={() => openReferralReissue(r)} title="Corregir referimiento">
                                <i className="fa fa-rotate me-1"></i> Corregir
                              </button>
                            )}
                            {canDeleteReferral(r, c, user, actingDoctorIds) && (
                              <button className="btn btn-sm bg-danger-light" onClick={() => requestReferralDelete(r)} title="Eliminar">
                                <i className="fa fa-times"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !showNewReferral && <EmptyState icon="fa-stethoscope" title="No hay referimientos" className="py-3" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Información Adicional */}
      {c.specialty_data && c.doctor?.specialty?.name?.toLowerCase().includes('cardiolog') && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title text-danger">
                  <i className="fa fa-heartbeat me-2"></i>Evaluación Cardiológica
                </h4>
                <div className="row mt-3">
                  {c.specialty_data.nyha_class && (
                    <div className="col-md-3">
                      <p className="text-muted mb-1">Clase Funcional NYHA</p>
                      <strong>Clase {c.specialty_data.nyha_class}</strong>
                    </div>
                  )}
                  {c.specialty_data.cardiac_exam && (
                    <div className="col-md-9">
                      <p className="text-muted mb-1">Examen Cardiovascular</p>
                      <strong>{c.specialty_data.cardiac_exam}</strong>
                    </div>
                  )}
                  {c.specialty_data.risk_factors && (
                    <div className="col-md-12 mt-2">
                      <p className="text-muted mb-1">Factores de Riesgo</p>
                      <strong>{c.specialty_data.risk_factors}</strong>
                    </div>
                  )}
                  {c.specialty_data.ecg_findings && (
                    <div className="col-md-12 mt-2">
                      <p className="text-muted mb-1">Hallazgos ECG</p>
                      <p>{c.specialty_data.ecg_findings}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gynecology Section */}
      {c.specialty_data && c.doctor?.specialty?.name?.toLowerCase().includes('ginec') && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card" style={{ borderLeft: '4px solid #7b2d8b' }}>
              <div className="card-body">
                <h4 className="card-title" style={{ color: '#7b2d8b' }}>
                  <i className="fa fa-venus me-2"></i>Evaluación Ginecológica
                </h4>
                <div className="row mt-3">
                  {(c.specialty_data.g_gestas || c.specialty_data.p_partos || c.specialty_data.a_abortos || c.specialty_data.c_cesareas) && (
                    <div className="col-md-6">
                      <p className="text-muted mb-1">Antecedentes Obstétricos (G-P-A-C)</p>
                      <strong>
                        G{c.specialty_data.g_gestas || 0} P{c.specialty_data.p_partos || 0} A{c.specialty_data.a_abortos || 0} C{c.specialty_data.c_cesareas || 0}
                      </strong>
                    </div>
                  )}
                  {c.specialty_data.fum && (
                    <div className="col-md-3">
                      <p className="text-muted mb-1">FUM</p>
                      <strong>{new Date(c.specialty_data.fum).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                    </div>
                  )}
                  {c.specialty_data.fpp && (
                    <div className="col-md-3">
                      <p className="text-muted mb-1">FPP</p>
                      <strong>{new Date(c.specialty_data.fpp).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                    </div>
                  )}
                  {c.specialty_data.menarquia && (
                    <div className="col-md-3 mt-2">
                      <p className="text-muted mb-1">Menarquia</p>
                      <strong>{c.specialty_data.menarquia} años</strong>
                    </div>
                  )}
                  {c.specialty_data.ciclo_menstrual && (
                    <div className="col-md-3 mt-2">
                      <p className="text-muted mb-1">Ciclo Menstrual</p>
                      <strong>{c.specialty_data.ciclo_menstrual}</strong>
                    </div>
                  )}
                  {c.specialty_data.ultimo_pap && (
                    <div className="col-md-3 mt-2">
                      <p className="text-muted mb-1">Último Pap</p>
                      <strong>{new Date(c.specialty_data.ultimo_pap).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                    </div>
                  )}
                  {c.specialty_data.metodo_anticonceptivo && (
                    <div className="col-md-3 mt-2">
                      <p className="text-muted mb-1">Método Anticonceptivo</p>
                      <strong>{c.specialty_data.metodo_anticonceptivo}</strong>
                    </div>
                  )}
                  {c.specialty_data.hallazgos_ginecologicos && (
                    <div className="col-md-12 mt-2">
                      <p className="text-muted mb-1">Hallazgos Ginecológicos</p>
                      <p>{c.specialty_data.hallazgos_ginecologicos}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Surgery Pre-op Section */}
      {c.specialty_data && c.doctor?.specialty?.name?.toLowerCase().includes('ciru') && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card" style={{ borderLeft: '4px solid #e67e22' }}>
              <div className="card-body">
                <h4 className="card-title" style={{ color: '#e67e22' }}>
                  <i className="fa fa-clipboard-list me-2"></i>Evaluación Quirúrgica
                </h4>
                <div className="row mt-3">
                  {c.specialty_data.asa_class && (
                    <div className="col-md-3">
                      <p className="text-muted mb-1">Clasificación ASA</p>
                      <strong>ASA {c.specialty_data.asa_class}</strong>
                    </div>
                  )}
                  {c.specialty_data.indicacion_quirurgica && (
                    <div className="col-md-9">
                      <p className="text-muted mb-1">Indicación Quirúrgica</p>
                      <strong>{c.specialty_data.indicacion_quirurgica}</strong>
                    </div>
                  )}
                  {c.specialty_data.surgical_risk_factors && (
                    <div className="col-md-12 mt-2">
                      <p className="text-muted mb-1">Factores de Riesgo Quirúrgico</p>
                      <strong>{c.specialty_data.surgical_risk_factors}</strong>
                    </div>
                  )}
                  {c.specialty_data.preop_notes && (
                    <div className="col-md-12 mt-2">
                      <p className="text-muted mb-1">Notas Preoperatorias</p>
                      <p>{c.specialty_data.preop_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pediatric Section */}
      {pediatricData && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card" style={{ borderLeft: '4px solid #0d9488' }}>
              <div className="card-body">
                <h4 className="card-title" style={{ color: '#0d9488' }}>
                  <i className="fa fa-child me-2"></i>Evaluación Pediátrica
                </h4>

                {/* Antecedentes Perinatales */}
                <div className="mt-3">
                  <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                    <i className="fa fa-calendar me-1"></i> Antecedentes Perinatales
                  </h5>
                  <div className="row mt-2">
                    {pediatricData.perinatal?.gestational_age_weeks != null && (
                      <div className="col-md-2">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Edad Gestacional</p>
                        <strong>{pediatricData.perinatal.gestational_age_weeks} sem</strong>
                      </div>
                    )}
                    {pediatricData.perinatal?.delivery_type && (
                      <div className="col-md-2">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo de Parto</p>
                        <strong>{pediatricData.perinatal.delivery_type}</strong>
                      </div>
                    )}
                    {pediatricData.perinatal?.birth_weight_g != null && (
                      <div className="col-md-2">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Peso al Nacer</p>
                        <strong>{pediatricData.perinatal.birth_weight_g} g</strong>
                      </div>
                    )}
                    {pediatricData.perinatal?.birth_height_cm != null && (
                      <div className="col-md-2">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Talla al Nacer</p>
                        <strong>{pediatricData.perinatal.birth_height_cm} cm</strong>
                      </div>
                    )}
                    {pediatricData.perinatal?.birth_head_cm != null && (
                      <div className="col-md-2">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>PC al Nacer</p>
                        <strong>{pediatricData.perinatal.birth_head_cm} cm</strong>
                      </div>
                    )}
                  </div>
                  <div className="row mt-1">
                    {pediatricData.perinatal?.apgar_1min != null && (
                      <div className="col-md-2">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Apgar 1 min</p>
                        <strong>{pediatricData.perinatal.apgar_1min}</strong>
                      </div>
                    )}
                    {pediatricData.perinatal?.apgar_5min != null && (
                      <div className="col-md-2">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Apgar 5 min</p>
                        <strong>{pediatricData.perinatal.apgar_5min}</strong>
                      </div>
                    )}
                    {pediatricData.perinatal?.perinatal_complications && (
                      <div className="col-md-8">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Complicaciones</p>
                        <strong>{pediatricData.perinatal.perinatal_complications}</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* Alimentación */}
                {pediatricData.feeding?.type && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-cutlery me-1"></i> Alimentación
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo de Lactancia</p>
                        <strong>{pediatricData.feeding.type === 'materna_exclusiva' ? 'Materna Exclusiva' :
                          pediatricData.feeding.type === 'materna_mixta' ? 'Materna Mixta' :
                          pediatricData.feeding.type === 'formula' ? 'Fórmula' :
                          pediatricData.feeding.type === 'ablactacion' ? 'Ablactación' :
                          pediatricData.feeding.type === 'complementaria' ? 'Complementaria' :
                          pediatricData.feeding.type}</strong>
                      </div>
                      {pediatricData.feeding?.ablactation_start_month != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Inicio Ablactación</p>
                          <strong>{pediatricData.feeding.ablactation_start_month} meses</strong>
                        </div>
                      )}
                      {pediatricData.feeding?.formula_name && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Fórmula</p>
                          <strong>{pediatricData.feeding.formula_name}</strong>
                        </div>
                      )}
                    </div>
                    {pediatricData.feeding?.food_allergies && (
                      <div className="mt-1">
                        <span style={{ fontSize: 12, color: '#666' }}>
                          <strong>Alergias Alimentarias:</strong> {pediatricData.feeding.food_allergies}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Crecimiento */}
                {(pediatricData.growth?.weight_kg != null || pediatricData.growth?.height_cm != null || pediatricData.growth?.head_cm != null) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-line-chart me-1"></i> Crecimiento y Percentiles
                    </h5>
                    <div className="row mt-2">
                      {pediatricData.growth?.weight_kg != null && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Peso Actual</p>
                          <strong>{pediatricData.growth.weight_kg} kg</strong>
                          {pedPercentileBadge(pediatricData.growth.weight_percentile)}
                        </div>
                      )}
                      {pediatricData.growth?.height_cm != null && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Talla Actual</p>
                          <strong>{pediatricData.growth.height_cm} cm</strong>
                          {pedPercentileBadge(pediatricData.growth.height_percentile)}
                        </div>
                      )}
                      {pediatricData.growth?.head_cm != null && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>PC Actual</p>
                          <strong>{pediatricData.growth.head_cm} cm</strong>
                          {pedPercentileBadge(pediatricData.growth.head_percentile)}
                        </div>
                      )}
                      {pediatricData.growth?.bmi != null && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>IMC</p>
                          <strong>{pediatricData.growth.bmi.toFixed(1)}</strong>
                          {pedPercentileBadge(pediatricData.growth.bmi_percentile)}
                        </div>
                      )}
                    </div>
                    {pediatricData.growth?.measurement_method && (
                      <div className="mt-1">
                        <span style={{ fontSize: 12, color: '#666' }}>
                          <strong>Método:</strong> {pediatricData.growth.measurement_method}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Hitos del Desarrollo */}
                {pediatricData.milestones && pediatricData.milestones.length > 0 && (() => {
                  const areaLabels: Record<string, string> = {
                    motor_gross: 'Motricidad Gruesa', motor_fine: 'Motricidad Fina',
                    language: 'Lenguaje', social: 'Social',
                  };
                  const areaIcons: Record<string, string> = {
                    motor_gross: 'fa-arrows', motor_fine: 'fa-hand-peace',
                    language: 'fa-comment', social: 'fa-users',
                  };
                  const areas: Record<string, Array<{ milestone: string; achieved: boolean }>> = {};
                  pediatricData.milestones.forEach((m: any) => {
                    if (!areas[m.area]) areas[m.area] = [];
                    areas[m.area].push(m);
                  });
                  const total = pediatricData.milestones.length;
                  const achieved = pediatricData.milestones.filter((m: any) => m.achieved).length;
                  const pct = total > 0 ? Math.round((achieved / total) * 100) : 0;
                  return (
                    <div className="mt-3">
                      <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                        <i className="fa fa-circle-check me-1"></i> Hitos del Desarrollo
                      </h5>
                      <div className="d-flex align-items-center mt-2">
                        <strong>{achieved}/{total}</strong>
                        <div className="progress ms-2" style={{ height: 8, flex: 1, maxWidth: 200 }}>
                          <div className="progress-bar" role="progressbar"
                            style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }}
                            aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} />
                        </div>
                        <span className="ms-2" style={{ fontSize: 12, color: '#666' }}>{pct}%</span>
                      </div>
                      <div className="row mt-2">
                        {Object.entries(areas).map(([area, items]) => (
                          <div className="col-md-6 mb-2" key={area}>
                            <h6 style={{ fontSize: 13 }}>
                              <i className={`fa ${areaIcons[area] || 'fa-circle-check'} me-1`}></i>
                              {areaLabels[area] || area}
                            </h6>
                            {items.map((m: any, i: number) => (
                              <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                                <i className={`fa ${m.achieved ? 'fa-check text-success' : 'fa-times text-muted'} me-1`} style={{ width: 14 }}></i>
                                {m.milestone}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                      {pediatricData.milestones_observations && (
                        <div className="mt-1">
                          <span style={{ fontSize: 12, color: '#666' }}>
                            <strong>Observaciones:</strong> {pediatricData.milestones_observations}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Esquema de Vacunación */}
                {pediatricData.vaccines && pediatricData.vaccines.length > 0 && (() => {
                  const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
                    completo: { label: 'Completo', color: '#10b981', bg: '#f0fdf4' },
                    incompleto: { label: 'Incompleto', color: '#f59e0b', bg: '#fffbeb' },
                    no_iniciado: { label: 'No iniciado', color: '#6b7280', bg: '#f3f4f6' },
                  };
                  const sv = pediatricData.vaccination_status || 'no_iniciado';
                  const st = statusConfig[sv] || statusConfig.no_iniciado;
                  const applied = pediatricData.vaccines.filter((v: any) => v.applied).length;
                  return (
                    <div className="mt-3">
                      <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                        <i className="fa fa-medkit me-1"></i> Esquema de Vacunación (PAI)
                      </h5>
                      <div className="d-flex align-items-center mt-2">
                        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, color: st.color, background: st.bg, border: `1px solid ${st.color}40` }}>
                          {st.label} · {applied}/{pediatricData.vaccines.length}
                        </span>
                      </div>
                      <div className="table-responsive mt-2">
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th>Vacuna</th>
                              <th>Dosis</th>
                              <th style={{ width: 60 }}>Aplicada</th>
                              <th style={{ width: 140 }}>Fecha</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pediatricData.vaccines.map((v: any, i: number) => (
                              <tr key={i}>
                                <td>{v.vaccine}</td>
                                <td>{v.dose}</td>
                                <td>{v.applied ? <i className="fa fa-check text-success"></i> : <i className="fa fa-times text-muted"></i>}</td>
                                <td>{formatDate(v.date)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {pediatricData.vaccination_observations && (
                        <div className="mt-1">
                          <span style={{ fontSize: 12, color: '#666' }}>
                            <strong>Observaciones:</strong> {pediatricData.vaccination_observations}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Internal Medicine Section */}
      {internalMedicineData && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card" style={{ borderLeft: '4px solid #0d9488' }}>
              <div className="card-body">
                <h4 className="card-title" style={{ color: '#0d9488' }}>
                  <i className="fa fa-stethoscope me-2"></i>Evaluación Medicina Interna
                </h4>

                {/* Diabetes */}
                {internalMedicineData.diabetes?.type && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-tint me-1"></i> Diabetes Mellitus
                    </h5>
                    <div className="row mt-2">
                      {internalMedicineData.diabetes.type && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo</p>
                          <strong>{internalMedicineData.diabetes.type}</strong>
                        </div>
                      )}
                      {internalMedicineData.diabetes.diagnosis_years != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Años de Dx</p>
                          <strong>{internalMedicineData.diabetes.diagnosis_years}</strong>
                        </div>
                      )}
                      {internalMedicineData.diabetes.hba1c != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>HbA1c</p>
                          <strong>{internalMedicineData.diabetes.hba1c}%</strong>
                          {internalMedicineData.diabetes.hba1c_date && (
                            <small className="text-muted d-block">({internalMedicineData.diabetes.hba1c_date})</small>
                          )}
                        </div>
                      )}
                      {internalMedicineData.diabetes.fasting_glucose != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Glucemia Ayunas</p>
                          <strong>{internalMedicineData.diabetes.fasting_glucose} mg/dL</strong>
                        </div>
                      )}
                    </div>
                    <div className="row mt-1">
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Examen de Pie</p>
                        <span>{internalMedicineData.diabetes.foot_exam_done ? <><i className="fa fa-check text-success me-1"></i>Sí</> : <><i className="fa fa-times text-muted me-1"></i>No</>}</span>
                        {internalMedicineData.diabetes.foot_exam_done && internalMedicineData.diabetes.foot_exam_findings && (
                          <small className="text-muted d-block">{internalMedicineData.diabetes.foot_exam_findings}</small>
                        )}
                      </div>
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Fondo de Ojo</p>
                        <span>{internalMedicineData.diabetes.fundoscopy_done ? <><i className="fa fa-check text-success me-1"></i>Sí</> : <><i className="fa fa-times text-muted me-1"></i>No</>}</span>
                        {internalMedicineData.diabetes.fundoscopy_done && internalMedicineData.diabetes.fundoscopy_findings && (
                          <small className="text-muted d-block">{internalMedicineData.diabetes.fundoscopy_findings}</small>
                        )}
                      </div>
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Cribado Nefropatía</p>
                        <span>{internalMedicineData.diabetes.nephropathy_screening_done ? <><i className="fa fa-check text-success me-1"></i>Sí</> : <><i className="fa fa-times text-muted me-1"></i>No</>}</span>
                        {internalMedicineData.diabetes.nephropathy_screening_done && internalMedicineData.diabetes.albuminuria != null && (
                          <small className="text-muted d-block">Albuminuria: {internalMedicineData.diabetes.albuminuria} mg/g</small>
                        )}
                      </div>
                    </div>
                    {internalMedicineData.diabetes.current_treatment?.length > 0 && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tratamiento Actual</p>
                          <strong>{internalMedicineData.diabetes.current_treatment.join(', ')}</strong>
                        </div>
                      </div>
                    )}
                    {internalMedicineData.diabetes.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {internalMedicineData.diabetes.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Hipertensión */}
                {internalMedicineData.hypertension?.stage && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-heartbeat me-1"></i> Hipertensión Arterial
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Estadio</p>
                        <strong>{internalMedicineData.hypertension.stage}</strong>
                        {internalMedicineData.hypertension.calculated_stage && internalMedicineData.hypertension.stage !== internalMedicineData.hypertension.calculated_stage && (
                          <small className="text-muted d-block">(Calculado: {internalMedicineData.hypertension.calculated_stage})</small>
                        )}
                      </div>
                      {internalMedicineData.hypertension.diagnosis_years != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Años de Dx</p>
                          <strong>{internalMedicineData.hypertension.diagnosis_years}</strong>
                        </div>
                      )}
                      {internalMedicineData.hypertension.avg_sbp != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>PAS Promedio</p>
                          <strong>{internalMedicineData.hypertension.avg_sbp} mmHg</strong>
                        </div>
                      )}
                      {internalMedicineData.hypertension.avg_dbp != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>PAD Promedio</p>
                          <strong>{internalMedicineData.hypertension.avg_dbp} mmHg</strong>
                        </div>
                      )}
                    </div>
                    {internalMedicineData.hypertension.target_organ_damage?.filter((o: any) => o.present).length > 0 && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Daño a Órgano Blanco</p>
                          <ul className="list-unstyled d-flex flex-wrap gap-3">
                            {internalMedicineData.hypertension.target_organ_damage.filter((o: any) => o.present).map((o: any, i: number) => (
                              <li key={i}><i className="fa fa-exclamation-triangle text-warning me-1"></i> {o.organ}{o.details ? `: ${o.details}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {internalMedicineData.hypertension.current_treatment?.length > 0 && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tratamiento Actual</p>
                          <strong>{internalMedicineData.hypertension.current_treatment.join(', ')}</strong>
                        </div>
                      </div>
                    )}
                    {internalMedicineData.hypertension.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {internalMedicineData.hypertension.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Dislipidemia */}
                {internalMedicineData.dyslipidemia && (internalMedicineData.dyslipidemia.ldl != null || internalMedicineData.dyslipidemia.hdl != null) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-line-chart me-1"></i> Dislipidemia
                    </h5>
                    <div className="row mt-2">
                      {internalMedicineData.dyslipidemia.ldl != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>LDL</p>
                          <strong>{internalMedicineData.dyslipidemia.ldl} mg/dL</strong>
                        </div>
                      )}
                      {internalMedicineData.dyslipidemia.hdl != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>HDL</p>
                          <strong>{internalMedicineData.dyslipidemia.hdl} mg/dL</strong>
                        </div>
                      )}
                      {internalMedicineData.dyslipidemia.triglycerides != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Triglicéridos</p>
                          <strong>{internalMedicineData.dyslipidemia.triglycerides} mg/dL</strong>
                        </div>
                      )}
                      {internalMedicineData.dyslipidemia.total_cholesterol != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Colesterol Total</p>
                          <strong>{internalMedicineData.dyslipidemia.total_cholesterol} mg/dL</strong>
                        </div>
                      )}
                      {internalMedicineData.dyslipidemia.lipid_profile_date && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Fecha Perfil</p>
                          <strong>{internalMedicineData.dyslipidemia.lipid_profile_date}</strong>
                        </div>
                      )}
                    </div>
                    {internalMedicineData.dyslipidemia.treatment && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tratamiento</p>
                          <strong>{internalMedicineData.dyslipidemia.treatment}</strong>
                        </div>
                      </div>
                    )}
                    {internalMedicineData.dyslipidemia.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {internalMedicineData.dyslipidemia.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Riesgo Cardiovascular */}
                {internalMedicineData.cv_risk && internalMedicineData.cv_risk.calculated_risk_percent != null && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-heart me-1"></i> Riesgo Cardiovascular
                    </h5>
                    <div className="d-flex align-items-center mt-2 gap-2">
                      <span style={{ display: 'inline-block', padding: '6px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#fff', background: (() => { const p = internalMedicineData.cv_risk.calculated_risk_percent!; return p < 10 ? '#10b981' : p < 20 ? '#f59e0b' : p < 30 ? '#f97316' : '#dc2626'; })() }}>
                        {internalMedicineData.cv_risk.calculated_risk_percent}% — {(() => { const p = internalMedicineData.cv_risk.calculated_risk_percent!; const labels: Record<string, string> = { Bajo: 'Bajo', Moderado: 'Moderado', Alto: 'Alto', 'Muy Alto': 'Muy Alto' }; return p < 10 ? 'Bajo' : p < 20 ? 'Moderado' : p < 30 ? 'Alto' : 'Muy Alto'; })()}
                      </span>
                    </div>
                    {internalMedicineData.cv_risk.risk_factors?.length > 0 && (
                      <div className="row mt-2">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Factores de Riesgo</p>
                          <div className="d-flex flex-wrap gap-2">
                            {internalMedicineData.cv_risk.risk_factors.map((f: string, i: number) => (
                              <span key={i} className="custom-badge status-grey">{f}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {internalMedicineData.cv_risk.risk_table && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small className="text-muted">Tabla: {internalMedicineData.cv_risk.risk_table}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Síndrome Metabólico */}
                {internalMedicineData.metabolic_syndrome && internalMedicineData.metabolic_syndrome.criteria_positive_count > 0 && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-balance-scale me-1"></i> Síndrome Metabólico
                    </h5>
                    <div className="d-flex align-items-center mt-2 gap-2">
                      <strong>{internalMedicineData.metabolic_syndrome.criteria_positive_count}/5 criterios</strong>
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#fff', background: internalMedicineData.metabolic_syndrome.diagnosis ? '#dc2626' : '#10b981' }}>
                        {internalMedicineData.metabolic_syndrome.diagnosis ? 'POSITIVO' : 'NEGATIVO'}
                      </span>
                    </div>
                    {internalMedicineData.metabolic_syndrome.waist_circumference_cm != null && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small className="text-muted">Perímetro Abdominal: {internalMedicineData.metabolic_syndrome.waist_circumference_cm} cm</small>
                        </div>
                      </div>
                    )}
                    {internalMedicineData.metabolic_syndrome.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {internalMedicineData.metabolic_syndrome.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Plan de Tratamiento */}
                {internalMedicineData.treatment_plan && (internalMedicineData.treatment_plan.hba1c_target || internalMedicineData.treatment_plan.sbp_target != null || internalMedicineData.treatment_plan.ldl_target || internalMedicineData.treatment_plan.next_appointment) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-file-lines me-1"></i> Plan de Tratamiento
                    </h5>
                    <div className="row mt-2">
                      {internalMedicineData.treatment_plan.hba1c_target && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Meta HbA1c</p>
                          <strong>{internalMedicineData.treatment_plan.hba1c_target}</strong>
                        </div>
                      )}
                      {internalMedicineData.treatment_plan.sbp_target != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Meta PAS</p>
                          <strong>&lt; {internalMedicineData.treatment_plan.sbp_target} mmHg</strong>
                        </div>
                      )}
                      {internalMedicineData.treatment_plan.dbp_target != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Meta PAD</p>
                          <strong>&lt; {internalMedicineData.treatment_plan.dbp_target} mmHg</strong>
                        </div>
                      )}
                      {internalMedicineData.treatment_plan.ldl_target && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Meta LDL</p>
                          <strong>{internalMedicineData.treatment_plan.ldl_target}</strong>
                        </div>
                      )}
                    </div>
                    <div className="row mt-1">
                      {internalMedicineData.treatment_plan.next_appointment && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Próxima Cita</p>
                          <strong>{new Date(internalMedicineData.treatment_plan.next_appointment).toLocaleDateString('es-DO')}</strong>
                        </div>
                      )}
                      {internalMedicineData.treatment_plan.next_labs && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Próximos Labs</p>
                          <strong>{new Date(internalMedicineData.treatment_plan.next_labs).toLocaleDateString('es-DO')}</strong>
                        </div>
                      )}
                    </div>
                    {internalMedicineData.treatment_plan.plan_notes && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Notas:</strong> {internalMedicineData.treatment_plan.plan_notes}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Traumatología Section */}
      {traumatologiaData && c.doctor?.specialty?.name?.toLowerCase().includes('traumatolog') && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card" style={{ borderLeft: '4px solid #ea580c' }}>
              <div className="card-body">
                <h4 className="card-title" style={{ color: '#ea580c' }}>
                  <i className="fa fa-bone me-2"></i>Evaluación Traumatológica
                </h4>

                {/* Fractura */}
                {traumatologiaData.fracture?.type && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-plus-circle me-1"></i> Fractura
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo</p>
                        <strong>{traumatologiaData.fracture.type}</strong>
                      </div>
                      {traumatologiaData.fracture.anatomic_location && (
                        <div className="col-md-4">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Localización Anatómica</p>
                          <strong>{traumatologiaData.fracture.anatomic_location}</strong>
                        </div>
                      )}
                      {traumatologiaData.fracture.side && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Lado</p>
                          <strong>{traumatologiaData.fracture.side}</strong>
                        </div>
                      )}
                      {traumatologiaData.fracture.open_closed && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo</p>
                          <strong>{traumatologiaData.fracture.open_closed === 'abierta' ? 'Abierta' : 'Cerrada'}</strong>
                        </div>
                      )}
                    </div>
                    {traumatologiaData.fracture.open_closed === 'abierta' && traumatologiaData.fracture.gustilo_classification && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Clasificación Gustilo</p>
                          <strong>{traumatologiaData.fracture.gustilo_classification}</strong>
                        </div>
                      </div>
                    )}
                    {traumatologiaData.fracture.mechanism && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Mecanismo</p>
                          <strong>{traumatologiaData.fracture.mechanism}</strong>
                        </div>
                      </div>
                    )}
                    {traumatologiaData.fracture.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {traumatologiaData.fracture.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tratamiento */}
                {traumatologiaData.treatment?.type && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-medkit me-1"></i> Tratamiento
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo</p>
                        <strong>{traumatologiaData.treatment.type}</strong>
                      </div>
                      {traumatologiaData.treatment.immobilization_type && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Inmovilización</p>
                          <strong>{traumatologiaData.treatment.immobilization_type}</strong>
                        </div>
                      )}
                      {traumatologiaData.treatment.surgical_plan && (
                        <div className="col-md-6">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Plan Quirúrgico</p>
                          <strong>{traumatologiaData.treatment.surgical_plan}</strong>
                        </div>
                      )}
                    </div>
                    {traumatologiaData.treatment.medications?.length > 0 && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Medicamentos</p>
                          <strong>{traumatologiaData.treatment.medications.join(', ')}</strong>
                        </div>
                      </div>
                    )}
                    {traumatologiaData.treatment.rehab_plan && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Plan de Rehabilitación</p>
                          <strong>{traumatologiaData.treatment.rehab_plan}</strong>
                        </div>
                      </div>
                    )}
                    {traumatologiaData.treatment.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {traumatologiaData.treatment.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Examen Físico Traumatológico */}
                {traumatologiaData.physical_exam?.inspection_findings && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-stethoscope me-1"></i> Examen Físico
                    </h5>
                    <div className="row mt-2">
                      {traumatologiaData.physical_exam.inspection_findings && (
                        <div className="col-md-4">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Inspección</p>
                          <strong>{traumatologiaData.physical_exam.inspection_findings}</strong>
                        </div>
                      )}
                      {traumatologiaData.physical_exam.palpation_findings && (
                        <div className="col-md-4">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Palpación</p>
                          <strong>{traumatologiaData.physical_exam.palpation_findings}</strong>
                        </div>
                      )}
                      {traumatologiaData.physical_exam.mobility_range && (
                        <div className="col-md-4">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Rango de Movilidad</p>
                          <strong>{traumatologiaData.physical_exam.mobility_range}</strong>
                        </div>
                      )}
                    </div>
                    {traumatologiaData.physical_exam.neurovascular_status && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Estado Neurovascular</p>
                          <strong>{traumatologiaData.physical_exam.neurovascular_status}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Imágenes */}
                {traumatologiaData.imaging?.studies?.length > 0 && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-image me-1"></i> Estudios de Imagen
                    </h5>
                    <div className="row mt-2">
                      <div className="col-12">
                        <strong>{traumatologiaData.imaging.studies.join(', ')}</strong>
                      </div>
                    </div>
                    {traumatologiaData.imaging.key_findings && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Hallazgos:</strong> {traumatologiaData.imaging.key_findings}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Observaciones Generales */}
                {traumatologiaData.general_observations && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-comment me-1"></i> Observaciones Generales
                    </h5>
                    <p className="mt-2">{traumatologiaData.general_observations}</p>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Oftalmología Section */}
      {oftalmologiaData && c.doctor?.specialty?.name?.toLowerCase().includes('oftalmolog') && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card" style={{ borderLeft: '4px solid #0d9488' }}>
              <div className="card-body">
                <h4 className="card-title" style={{ color: '#0d9488' }}>
                  <i className="fa fa-eye me-2"></i>Evaluación Oftalmológica
                </h4>

                {/* Agudeza Visual */}
                {oftalmologiaData.visual_acuity && (oftalmologiaData.visual_acuity.sc_od || oftalmologiaData.visual_acuity.sc_oi) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-eye me-1"></i> Agudeza Visual
                    </h5>
                    <div className="row mt-2">
                      {oftalmologiaData.visual_acuity.sc_od && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>SC OD</p>
                          <strong>{oftalmologiaData.visual_acuity.sc_od}</strong>
                        </div>
                      )}
                      {oftalmologiaData.visual_acuity.sc_oi && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>SC OI</p>
                          <strong>{oftalmologiaData.visual_acuity.sc_oi}</strong>
                        </div>
                      )}
                      {oftalmologiaData.visual_acuity.cc_od && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>CC OD</p>
                          <strong>{oftalmologiaData.visual_acuity.cc_od}</strong>
                        </div>
                      )}
                      {oftalmologiaData.visual_acuity.cc_oi && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>CC OI</p>
                          <strong>{oftalmologiaData.visual_acuity.cc_oi}</strong>
                        </div>
                      )}
                    </div>
                    <div className="row mt-1">
                      {oftalmologiaData.visual_acuity.near_vision && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Visión Cercana</p>
                          <strong>{oftalmologiaData.visual_acuity.near_vision}</strong>
                        </div>
                      )}
                      {oftalmologiaData.visual_acuity.pinhole_improves && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Agujero Estenopeico</p>
                          <strong>{oftalmologiaData.visual_acuity.pinhole_improves}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Refracción */}
                {oftalmologiaData.refraction && (oftalmologiaData.refraction.sphere_od || oftalmologiaData.refraction.sphere_oi) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-sliders me-1"></i> Refracción
                    </h5>
                    <div className="row mt-2">
                      {oftalmologiaData.refraction.sphere_od && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Esf OD</p>
                          <strong>{oftalmologiaData.refraction.sphere_od}</strong>
                        </div>
                      )}
                      {oftalmologiaData.refraction.sphere_oi && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Esf OI</p>
                          <strong>{oftalmologiaData.refraction.sphere_oi}</strong>
                        </div>
                      )}
                      {oftalmologiaData.refraction.cylinder_od && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Cil OD</p>
                          <strong>{oftalmologiaData.refraction.cylinder_od}</strong>
                        </div>
                      )}
                      {oftalmologiaData.refraction.cylinder_oi && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Cil OI</p>
                          <strong>{oftalmologiaData.refraction.cylinder_oi}</strong>
                        </div>
                      )}
                      {oftalmologiaData.refraction.axis_od && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Eje OD</p>
                          <strong>{oftalmologiaData.refraction.axis_od}°</strong>
                        </div>
                      )}
                      {oftalmologiaData.refraction.axis_oi && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Eje OI</p>
                          <strong>{oftalmologiaData.refraction.axis_oi}°</strong>
                        </div>
                      )}
                    </div>
                    <div className="row mt-1">
                      {oftalmologiaData.refraction.add_od && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Add OD</p>
                          <strong>{oftalmologiaData.refraction.add_od}</strong>
                        </div>
                      )}
                      {oftalmologiaData.refraction.add_oi && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Add OI</p>
                          <strong>{oftalmologiaData.refraction.add_oi}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Segmento Anterior */}
                {oftalmologiaData.anterior_segment && oftalmologiaData.anterior_segment.conjunctiva && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-search me-1"></i> Segmento Anterior
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Conjuntiva</p>
                        <strong>{oftalmologiaData.anterior_segment.conjunctiva}</strong>
                      </div>
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Córnea</p>
                        <strong>{oftalmologiaData.anterior_segment.cornea}</strong>
                      </div>
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Cámara Anterior</p>
                        <strong>{oftalmologiaData.anterior_segment.anterior_chamber}</strong>
                      </div>
                    </div>
                    <div className="row mt-1">
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Iris</p>
                        <strong>{oftalmologiaData.anterior_segment.iris}</strong>
                      </div>
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Pupila</p>
                        <strong>{oftalmologiaData.anterior_segment.pupil}</strong>
                      </div>
                      <div className="col-md-6">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Cristalino</p>
                        <strong>{oftalmologiaData.anterior_segment.lens}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* Segmento Posterior */}
                {oftalmologiaData.posterior_segment && oftalmologiaData.posterior_segment.vitreous && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-eye-slash me-1"></i> Segmento Posterior
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Vítreo</p>
                        <strong>{oftalmologiaData.posterior_segment.vitreous}</strong>
                      </div>
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Disco Óptico</p>
                        <strong>{oftalmologiaData.posterior_segment.optic_disc}</strong>
                      </div>
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Mácula</p>
                        <strong>{oftalmologiaData.posterior_segment.macula}</strong>
                      </div>
                    </div>
                    <div className="row mt-1">
                      <div className="col-md-6">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Retina</p>
                        <strong>{oftalmologiaData.posterior_segment.retina}</strong>
                      </div>
                      <div className="col-md-6">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Vasos</p>
                        <strong>{oftalmologiaData.posterior_segment.vessels}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* PIO */}
                {oftalmologiaData.intraocular_pressure && (oftalmologiaData.intraocular_pressure.od != null || oftalmologiaData.intraocular_pressure.oi != null) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-tachometer me-1"></i> Presión Intraocular
                    </h5>
                    <div className="row mt-2">
                      {oftalmologiaData.intraocular_pressure.od != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>PIO OD</p>
                          <strong>{oftalmologiaData.intraocular_pressure.od} mmHg</strong>
                        </div>
                      )}
                      {oftalmologiaData.intraocular_pressure.oi != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>PIO OI</p>
                          <strong>{oftalmologiaData.intraocular_pressure.oi} mmHg</strong>
                        </div>
                      )}
                      {oftalmologiaData.intraocular_pressure.method && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Método</p>
                          <strong>{oftalmologiaData.intraocular_pressure.method}</strong>
                        </div>
                      )}
                      {oftalmologiaData.intraocular_pressure.time && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Horario</p>
                          <strong>{oftalmologiaData.intraocular_pressure.time}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Diagnóstico */}
                {oftalmologiaData.diagnosis_oft?.primary && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-medkit me-1"></i> Diagnóstico
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-4">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Diagnóstico</p>
                        <strong>{oftalmologiaData.diagnosis_oft.primary}</strong>
                      </div>
                      {oftalmologiaData.diagnosis_oft.od_oi && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>OD/OI</p>
                          <strong>{oftalmologiaData.diagnosis_oft.od_oi}</strong>
                        </div>
                      )}
                      {oftalmologiaData.diagnosis_oft.icd10_code && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>CIE-10</p>
                          <strong>{oftalmologiaData.diagnosis_oft.icd10_code}</strong>
                        </div>
                      )}
                      {oftalmologiaData.diagnosis_oft.type && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo</p>
                          <strong>{oftalmologiaData.diagnosis_oft.type}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tratamiento */}
                {(oftalmologiaData.treatment_oft?.eye_drops || oftalmologiaData.treatment_oft?.oral_medication) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-file-lines me-1"></i> Tratamiento
                    </h5>
                    {oftalmologiaData.treatment_oft.eye_drops && (
                      <div className="row mt-2">
                        <div className="col-md-4">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Gotas</p>
                          <strong>{oftalmologiaData.treatment_oft.eye_drops}</strong>
                          {oftalmologiaData.treatment_oft.eye_drops_frequency && (
                            <small className="d-block text-muted">C/{oftalmologiaData.treatment_oft.eye_drops_frequency}</small>
                          )}
                        </div>
                      </div>
                    )}
                    {oftalmologiaData.treatment_oft.oral_medication && (
                      <div className="row mt-1">
                        <div className="col-md-6">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Medicación Oral</p>
                          <strong>{oftalmologiaData.treatment_oft.oral_medication}</strong>
                        </div>
                      </div>
                    )}
                    {oftalmologiaData.treatment_oft.surgery_indicated && (
                      <div className="row mt-2">
                        <div className="col-12">
                          <span className="custom-badge status-orange"><i className="fa fa-plus-circle me-1"></i> Cirugía Indicada</span>
                          {oftalmologiaData.treatment_oft.surgery_type && (
                            <strong className="ms-2">{oftalmologiaData.treatment_oft.surgery_type}</strong>
                          )}
                        </div>
                      </div>
                    )}
                    {oftalmologiaData.treatment_oft.glasses_prescription && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <span className="custom-badge status-blue"><i className="fa fa-eye me-1"></i> Prescripción de Lentes</span>
                        </div>
                      </div>
                    )}
                    {oftalmologiaData.treatment_oft.follow_up_days != null && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Seguimiento</p>
                          <strong>{oftalmologiaData.treatment_oft.follow_up_days} días</strong>
                        </div>
                      </div>
                    )}
                    {oftalmologiaData.treatment_oft.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {oftalmologiaData.treatment_oft.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dermatología Section */}
      {dermatologiaData && c.doctor?.specialty?.name?.toLowerCase().includes('dermatolog') && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card" style={{ borderLeft: '4px solid #8b5cf6' }}>
              <div className="card-body">
                <h4 className="card-title" style={{ color: '#8b5cf6' }}>
                  <i className="fa fa-paint-brush me-2"></i>Evaluación Dermatológica
                </h4>

                {/* Lesión */}
                {dermatologiaData.lesion?.type && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-eye me-1"></i> Lesión
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tipo</p>
                        <strong>{dermatologiaData.lesion.type}</strong>
                      </div>
                      {dermatologiaData.lesion.size_mm != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tamaño</p>
                          <strong>{dermatologiaData.lesion.size_mm} mm</strong>
                        </div>
                      )}
                      {dermatologiaData.lesion.color && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Color</p>
                          <strong>{dermatologiaData.lesion.color}</strong>
                        </div>
                      )}
                      {dermatologiaData.lesion.border && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Borde</p>
                          <strong>{dermatologiaData.lesion.border}</strong>
                        </div>
                      )}
                      {dermatologiaData.lesion.surface && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Superficie</p>
                          <strong>{dermatologiaData.lesion.surface}</strong>
                        </div>
                      )}
                    </div>
                    <div className="row mt-1">
                      {dermatologiaData.lesion.distribution && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Distribución</p>
                          <strong>{dermatologiaData.lesion.distribution}</strong>
                        </div>
                      )}
                      {dermatologiaData.lesion.number_of_lesions && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Número</p>
                          <strong>{dermatologiaData.lesion.number_of_lesions}</strong>
                        </div>
                      )}
                      {dermatologiaData.lesion.symptom && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Síntoma</p>
                          <strong>{dermatologiaData.lesion.symptom}</strong>
                        </div>
                      )}
                      {dermatologiaData.lesion.anatomic_location && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Localización</p>
                          <strong>{dermatologiaData.lesion.anatomic_location}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Historia */}
                {dermatologiaData.history?.onset && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-history me-1"></i> Historia
                    </h5>
                    <div className="row mt-2">
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Inicio</p>
                        <strong>{dermatologiaData.history.onset}</strong>
                      </div>
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Evolución</p>
                        <strong>{dermatologiaData.history.evolution}</strong>
                      </div>
                      <div className="col-md-3">
                        <p className="text-muted mb-1" style={{ fontSize: 12 }}>Episodios Previos</p>
                        <strong>{dermatologiaData.history.previous_episodes ? 'Sí' : 'No'}</strong>
                      </div>
                    </div>
                    {dermatologiaData.history.previous_diagnosis && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Diagnóstico Previo</p>
                          <strong>{dermatologiaData.history.previous_diagnosis}</strong>
                        </div>
                      </div>
                    )}
                    {dermatologiaData.history.previous_treatment && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tratamiento Previo</p>
                          <strong>{dermatologiaData.history.previous_treatment}</strong>
                        </div>
                      </div>
                    )}
                    {dermatologiaData.history.trigger_factors && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Factores Desencadenantes:</strong> {dermatologiaData.history.trigger_factors}</small>
                        </div>
                      </div>
                    )}
                    {dermatologiaData.history.allergies && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Alergias:</strong> {dermatologiaData.history.allergies}</small>
                        </div>
                      </div>
                    )}
                    {dermatologiaData.history.family_history && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Historia Familiar:</strong> {dermatologiaData.history.family_history}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Dermatoscopía */}
                {dermatologiaData.dermoscopy?.performed && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-search-plus me-1"></i> Dermatoscopía
                    </h5>
                    <div className="row mt-2">
                      {dermatologiaData.dermoscopy.pattern && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Patrón</p>
                          <strong>{dermatologiaData.dermoscopy.pattern}</strong>
                        </div>
                      )}
                      {dermatologiaData.dermoscopy.ABCD_rule_score != null && (
                        <div className="col-md-2">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>ABCD</p>
                          <strong>{dermatologiaData.dermoscopy.ABCD_rule_score}</strong>
                        </div>
                      )}
                      {dermatologiaData.dermoscopy.melanoma_suspected && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Sospecha Melanoma</p>
                          <strong className="text-danger">Sí</strong>
                        </div>
                      )}
                      {dermatologiaData.dermoscopy.biopsy_recommended && (
                        <div className="col-md-3">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Biopsia Recomendada</p>
                          <strong>Sí</strong>
                        </div>
                      )}
                    </div>
                    {dermatologiaData.dermoscopy.findings && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Hallazgos:</strong> {dermatologiaData.dermoscopy.findings}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tratamiento */}
                {(dermatologiaData.treatment_derma?.topical || dermatologiaData.treatment_derma?.systemic) && (
                  <div className="mt-3">
                    <h5 className="text-muted" style={{ fontSize: 14, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <i className="fa fa-medkit me-1"></i> Tratamiento
                    </h5>
                    {dermatologiaData.treatment_derma.topical && (
                      <div className="row mt-2">
                        <div className="col-md-6">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Tópico</p>
                          <strong>{dermatologiaData.treatment_derma.topical}</strong>
                        </div>
                      </div>
                    )}
                    {dermatologiaData.treatment_derma.systemic && (
                      <div className="row mt-1">
                        <div className="col-md-6">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Sistémico</p>
                          <strong>{dermatologiaData.treatment_derma.systemic}</strong>
                        </div>
                      </div>
                    )}
                    {(dermatologiaData.treatment_derma.phototherapy || dermatologiaData.treatment_derma.cryotherapy || dermatologiaData.treatment_derma.laser || dermatologiaData.treatment_derma.surgical_excision) && (
                      <div className="row mt-2">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Procedimientos</p>
                          <div className="d-flex flex-wrap gap-2">
                            {dermatologiaData.treatment_derma.phototherapy && <span className="custom-badge status-blue">Fototerapia</span>}
                            {dermatologiaData.treatment_derma.cryotherapy && <span className="custom-badge status-purple">Crioterapia</span>}
                            {dermatologiaData.treatment_derma.laser && <span className="custom-badge status-orange">Láser</span>}
                            {dermatologiaData.treatment_derma.surgical_excision && <span className="custom-badge status-grey">Escisión Quirúrgica</span>}
                          </div>
                        </div>
                      </div>
                    )}
                    {dermatologiaData.treatment_derma.follow_up_weeks != null && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <p className="text-muted mb-1" style={{ fontSize: 12 }}>Seguimiento</p>
                          <strong>{dermatologiaData.treatment_derma.follow_up_weeks} semanas</strong>
                        </div>
                      </div>
                    )}
                    {dermatologiaData.treatment_derma.observations && (
                      <div className="row mt-1">
                        <div className="col-12">
                          <small style={{ color: '#666' }}><strong>Observaciones:</strong> {dermatologiaData.treatment_derma.observations}</small>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {(c.companion_name || c.requires_admission) && (
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title"><i className="fa fa-info-circle me-2"></i>Información Adicional</h4>
                <div className="row mt-3">
                  {c.companion_name && (
                    <div className="col-md-6">
                      <p><strong>Acompañante:</strong> {c.companion_name}
                        {c.companion_relationship && ` (${c.companion_relationship})`}</p>
                    </div>
                  )}
                  {c.requires_admission && (
                    <div className="col-md-6">
                      <div className="alert alert-warning mb-0">
                        <strong><i className="fa fa-hospital me-1"></i> Requiere Admisión</strong>
                        {c.admission_reason && <p className="mb-0 mt-1">{c.admission_reason}</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reissuingRx && (
        <ReissueDialog
          title="Corregir receta"
          recordLabel="la receta"
          voidedSummary={`${reissuingRx.medication_name} ${reissuingRx.dosage} — ${reissuingRx.frequency} — ${reissuingRx.duration}`}
          fields={PRESCRIPTION_REISSUE_FIELDS}
          initialValues={{
            medication_name: reissuingRx.medication_name, dosage: reissuingRx.dosage, frequency: reissuingRx.frequency,
            duration: reissuingRx.duration, instructions: reissuingRx.instructions || '',
          }}
          onSubmit={async (reason, values) => {
            await consultationPrescriptionService.reissue(reissuingRx.id, { reason, ...values } as Parameters<typeof consultationPrescriptionService.reissue>[1]);
            setReissuingRx(null);
            fetchConsultation(consultationId);
          }}
          onCancel={() => setReissuingRx(null)}
        />
      )}

      {reissuingReferral && (
        <ReissueDialog
          title="Corregir referimiento"
          recordLabel="el referimiento"
          voidedSummary={`${reissuingReferral.referred_specialty?.name || 'Especialidad'} — ${reissuingReferral.reason}`}
          fields={(values) => referralReissueFields(
            specialties, reissuingReferral, reissueDoctors[Number(values.referred_specialty_id)] ?? [], values.referred_specialty_id,
          )}
          initialValues={referralReissueInitialValues(reissuingReferral)}
          onChange={(values) => loadReissueDoctors(Number(values.referred_specialty_id))}
          onSubmit={async (voidReason, values) => {
            await medicalReferralService.reissue(reissuingReferral.id, toReferralReissueRequest(reissuingReferral, voidReason, values));
            setReissuingReferral(null);
            loadReferrals();
          }}
          onCancel={() => setReissuingReferral(null)}
        />
      )}

      {showAmendmentDrawer && (
        <AmendmentDrawer
          show
          consultation={c}
          amendments={amendments}
          onClose={() => setShowAmendmentDrawer(false)}
          onChanged={() => { loadAmendments(); fetchConsultation(consultationId); }}
        />
      )}

      <ConfirmModal
        show={pendingDiagnosisDelete !== null}
        title="Eliminar diagnóstico"
        body={`¿Eliminar el diagnóstico "${pendingDiagnosisDelete?.diagnosis?.description || pendingDiagnosisDelete?.diagnosis?.code || ''}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deletingDiagnosis}
        onConfirm={confirmDiagnosisDelete}
        onCancel={cancelDiagnosisDelete}
      />

      <ConfirmModal
        show={pendingPrescriptionDelete !== null}
        title="Eliminar receta"
        body={`¿Eliminar la receta de "${pendingPrescriptionDelete?.medication_name || ''}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deletingPrescription}
        onConfirm={confirmPrescriptionDelete}
        onCancel={cancelPrescriptionDelete}
      />

      <ConfirmModal
        show={pendingReferralDelete !== null}
        title="Eliminar referimiento"
        body={`¿Eliminar el referimiento a ${pendingReferralDelete?.referred_specialty?.name || 'la especialidad seleccionada'}? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deletingReferral}
        onConfirm={confirmReferralDelete}
        onCancel={cancelReferralDelete}
      />

      <ConfirmModal
        show={pendingImagingOrderDelete !== null}
        title="Eliminar orden de imagen"
        body={`¿Eliminar la orden de imagen "${pendingImagingOrderDelete?.imaging_study?.name || ''}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deletingImagingOrder}
        onConfirm={confirmImagingOrderDelete}
        onCancel={cancelImagingOrderDelete}
      />

      <ConfirmModal
        show={pendingLabOrderDelete !== null}
        title="Eliminar orden de laboratorio"
        body={`¿Eliminar la orden de laboratorio "${pendingLabOrderDelete?.panel?.name || pendingLabOrderDelete?.test?.name || pendingLabOrderDelete?.test_name || ''}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deletingLabOrder}
        onConfirm={confirmLabOrderDelete}
        onCancel={cancelLabOrderDelete}
      />
    </>
  );
};

export default ConsultationDetailPage;